/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Alert,
  Box,
  Typography,
  Chip,
  Tooltip,
  CircularProgress,
  Button,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
} from '@mui/material';
import {
  Business as OdooIcon,
  CheckCircle as CheckIcon,
  Cancel as FailIcon,
  ErrorOutline as ErrorOutlineIcon,
  ReportProblemOutlined as ReportProblemOutlinedIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  Incident,
  IncidentGroup,
  derivePolicyNotificationRoutes,
  type IncidentGroupActionRequest,
  type IncidentReviewOutcome,
  type PolicyNotificationRoute,
} from '../services/incidentsApi';
import {
  applyObservationEpisodeSearchParam,
  observationReviewIncidentId,
  observationReviewOpenOptions,
} from '../domain/incidents/observationEpisodeRow';
import { api, ensureUiScope } from '../services/api';
import { deriveOpsState } from '../services/opsState';
import { buildContextBundle } from '../services/contextBundle';
import { Device } from '../services/devicesApi';
import {
  executeRunbookServer,
} from '../services/opsCopilot';
import { useAuth } from '../contexts/AuthContext';
import {
  canManageIncidents,
  canOpenIncidentsArtifactEntry,
  canUseAdvancedIncidentsUi,
  canUseIncidentOpsAutomation,
  hasCapability as hasAccessCapability,
} from '../auth/access';
import { mergeNavContext, readNavContext, writeNavContext } from '../utils/navContext';
import { buildScopedIncidentsPath } from '../utils/incidentsNavigation';
import {
  activateFullCaseHistoryReviewParams,
  applyAfterSignedScanTimelineChange,
  filterIncidentsInAfterSignedScanWindow,
  incidentObservationTimestamp,
  isFullCaseHistoryReviewScope,
  resolveEffectiveAfterSignedScanReviewContext,
  resolveAfterSignedScanWindowBounds,
  type AfterSignedScanTimelineChange,
} from '../domain/incidents/afterSignedScanReview';
import {
  resolveFullCaseHistoryTimeBounds,
  resolveReviewWindowContext,
} from '../domain/incidents/reviewWindowContext';
import { deriveReviewerCaseState, reviewerCaseOpenedDays } from '../domain/incidents/reviewerCaseState';
import { formatFollowUpStateLabel } from '../utils/incidentStateLabels';
import { getWarehouseCapabilityProfile } from '../utils/warehouseNavigation';
import { getWebSummitDemoScenarioById, WEB_SUMMIT_DEMO_CAMERA_SOURCE } from './demo/webSummitDemoScenarios';
import { resolveIncidentWorkflowProblemContext } from '../domain/safetyCases/workflowGlueNavigation';
import { useNavigate, useSearchParams } from 'react-router-dom';
import IncidentCreateDialog from '../components/incidents/IncidentCreateDialog';
import IncidentEvidencePopover from '../components/incidents/IncidentEvidencePopover';
import IncidentDrawer from '../components/incidents/IncidentDrawer';
import IncidentResolveDialog from '../components/incidents/IncidentResolveDialog';
import IncidentSuppressDialog from '../components/incidents/IncidentSuppressDialog';
import IncidentGroupPatternActionDialog, {
  type IncidentGroupPatternActionMode,
} from '../components/incidents/IncidentGroupPatternActionDialog';
import { resolveOperatorActorId } from '../domain/incidents/incidentOwnershipQueueFilter';
import {
  applyLocalPatternOwnerAssignment,
  readPatternOwnerLabel,
  readPatternOwnerTeamLabel,
} from '../domain/incidents/incidentOwnershipSemantics';
import IncidentThresholdDialog from '../components/incidents/IncidentThresholdDialog';
import IncidentHeader from '../components/incidents/IncidentHeader';
import IncidentToolbar from '../components/incidents/IncidentToolbar';
import HeroMetricRow from '../components/incidents/HeroMetricRow';
import InsightBand from '../components/incidents/InsightBand';
import IncidentFiltersBar from '../components/incidents/IncidentFiltersBar';
import IncidentTable from '../components/incidents/IncidentTable';
import IncidentQueueBlock from '../components/incidents/IncidentQueueBlock';
import {
  resolveActiveReviewWindowIso,
  toIsoFromLocalInput as _toIsoFromLocalInput,
  toLocalInputValue as _toLocalInputValue,
  type IncidentTimeRange,
  useIncidentsTimeRange,
} from './incidents/useIncidentsTimeRange';
import {
  useIncidentDrawerState,
} from './incidents/useIncidentDrawerState';
import {
  useIncidentsFilters,
  type OperatorPreset,
} from './incidents/useIncidentsFilters';
import {
  impliedIncidentTableMode,
  readIncidentTableModeParam,
  type IncidentTableMode,
} from './incidents/incidentTableMode';
import {
  useIncidentsUiMode,
} from './incidents/useIncidentsUiMode';
import {
  useIncidentDrawerData,
} from './incidents/useIncidentDrawerData';
import {
  useIncidentsData,
} from './incidents/useIncidentsData';
import {
  useIncidentDrawerOperations,
} from './incidents/useIncidentDrawerOperations';
import {
  useIncidentEvidence,
} from './incidents/useIncidentEvidence';
import {
  useIncidentsReferenceData,
} from './incidents/useIncidentsReferenceData';
import {
  useIncidentsPageActions,
} from './incidents/useIncidentsPageActions';
import {
  useIncidentsTableColumns,
} from './incidents/useIncidentsTableColumns';
import {
  useIncidentsPageOrchestration,
} from './incidents/useIncidentsPageOrchestration';
import { useGroupedPatternReviewActions } from './incidents/useGroupedPatternReviewActions';
import { incidentTypeLabel, KNOWN_INCIDENT_TYPE_KEYS } from './incidents/incidentTypeLabels';
import { incidentDisplayTitleForRow } from './incidents/incidentDisplayModel';
import {
  resolveRepresentativeIncidentIdForReview,
} from '../domain/incidents/incidentPatternSemantics';
import {
  isObservationDispositionMode,
  isPatternCaseReviewMode,
  resolveIncidentReviewSurfaceMode,
  openFullProofReviewHop,
} from '../domain/incidents/incidentReviewSurfaceMode';
import { groupedEvidenceReviewResolvePresentation } from '../domain/incidents/groupedRepresentativeAuditCopy';
import { observationDispositionResolvePresentation } from '../domain/incidents/observationDispositionCopy';
import {
  buildHazardClassReportPath,
  resolveIncidentHazardClassKey,
} from '../domain/incidents/incidentHazardClass';
import { getIncidentPatternReadVolumeCount } from '../domain/incidents/patternReviewScopeSemantics';
import type { PatternActionViewScope } from '../domain/incidents/patternActionSelectionSet';
import {
  patternGroupActionStartedMessage,
  patternGroupActionSuccessMessage,
  runPatternGroupActionInBackground,
} from '../domain/incidents/patternGroupActionBackground';
import { IncidentsReviewPage } from './incidents/IncidentsReviewPage';
import { useIncidentsReviewPage } from './incidents/useIncidentsReviewPage';
import { useRiskPatternWorkflowReviewContext } from './incidents/useRiskPatternWorkflowReviewContext';
import { useIncidentsProofPackageNavigation } from './incidents/useIncidentsProofPackageNavigation';
import { buildRiskPatternWorkflowReviewActions } from '../domain/incidents/riskPatternWorkflowReviewActions';
import { resolveRiskPatternReviewPageState } from '../domain/incidents/riskPatternWorkflowReviewContext';
import { buildPolicyByIdMap, buildZonesByIdMap, resolvePackKeyForGroup } from './analytics/packCoverageModel';
import { PACK_BY_KEY, readPolicyUiContext, type PackKey } from '../domain/policies/policyPacks';

const GROUPED_REVIEW_DIGEST_DISMISS_KEY = 'edgentik.incidents.groupedReview.digest.dismissed';

const _isUuid = (s: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test((s || '').trim());

const _titleCase = (s: string): string => {
  const t = String(s || '').trim();
  if (!t) return '';
  return t
    .replace(/_/g, ' ')
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(' ');
};

const _scopedSiteId = (): string => {
  try {
    const scope = ensureUiScope();
    return String(scope.siteId || '').trim();
  } catch {
    return '';
  }
};

const _scopedOrgId = (): string => {
  try {
    const scope = ensureUiScope();
    return String(scope.orgId || '').trim();
  } catch {
    return '';
  }
};

const _shortId = (s: string): string => {
  const t = String(s || '').trim();
  if (!t) return '';
  // Hide timestamp-ish suffixes like warehouse-1_1765924969
  const parts = t.split('_');
  return parts[0] || t;
};

const _warehouseUnitLabel = (deviceIdRaw: string): string => {
  const base = _shortId(deviceIdRaw);
  // Prefer a stable, operator-friendly label derived from device_id.
  // Example device_id: "warehouse-1_1765924969293" -> base "warehouse-1" -> "Warehouse Unit 1"
  const m = /^warehouse-(\d+)$/i.exec(base);
  if (m && m[1]) return `Warehouse Unit ${m[1]}`;
  return base || deviceIdRaw;
};

const _renderAllLabel = (kind: string, value: any, labelForValue?: (id: string) => string): React.ReactNode => {
  const v = String(value || '').trim();
  if (!v) return <em title={`All ${kind}`}>All</em>;
  try {
    const lbl = labelForValue ? String(labelForValue(v) || '').trim() : '';
    return lbl || v;
  } catch {
    return v;
  }
};

const _isSelectableDeviceForIncidentsFilter = (d: Device | null | undefined): boolean => {
  if (!d) return false;
  const deviceId = String((d as any).device_id || '').trim();
  if (!deviceId) return false;

  // Best-effort exclusion for pseudo devices/camera records.
  const base = (deviceId.split('_')[0] || deviceId).trim().toLowerCase();
  if (base.startsWith('cam-') || base.startsWith('cam_') || base.startsWith('camera-') || base.startsWith('camera_')) return false;
  const dt = String((d as any).device_type || (d as any).type || '').trim().toLowerCase();
  if (dt === 'camera') return false;

  return true;
};

const _deviceFilterLabel = (d: Device): string => {
  const deviceIdRaw = String((d as any).device_id || '').trim();
  if (!deviceIdRaw) return '';
  const base = _shortId(deviceIdRaw);
  const m = /^warehouse-(\d+)$/i.exec(base);
  if (m && m[1]) return `Warehouse Unit ${m[1]}`;
  const name = String((d as any).name || '').trim();
  const md: any = (d as any)?.device_metadata || {};
  const ip = String((d as any).ip_address || md?.ip_address || md?.health_api_host || '').trim();
  const core = name || base || deviceIdRaw;
  return ip ? core : `${core} (no IP)`;
};

/** Policy display name from list/join or envelope; falls back to policy_id. */
const _incidentPolicyLabel = (row: Record<string, unknown> | Incident | null | undefined): string => {
  if (!row) return '—';
  const r = row as any;
  const name = String(r.policy_name || r.triggered_by?.policy_name || '').trim();
  const pid = String(r.policy_id || r.triggered_by?.policy_id || '').trim();
  const displayNames: Record<string, string> = {
    demo5acc_near_moving: 'Forklift-pedestrian proximity',
    demo5acc_pedestrian: 'Pedestrian in forklift aisle',
  };
  for (const rawPolicyIdentifier of [name, pid]) {
    const displayName = displayNames[rawPolicyIdentifier.toLowerCase()];
    if (displayName) return displayName;
  }
  return name || pid || '—';
};

const _ppeMissingItemsFromIncident = (inc: Incident | null | undefined): string[] => {
  if (!inc) return [];
  if ((inc.incident_type || inc.title) !== 'ppe_violation') return [];

  const metrics = (inc.metrics as any) || {};
  const attrs = (inc.metrics as any)?.attributes;

  const stabilizedMissing =
    attrs?.missing_ppe_stabilized ??
    metrics?.missing_ppe_stabilized ??
    metrics?.missing_ppe ??
    metrics?.missing_items;
  if (Array.isArray(stabilizedMissing) && stabilizedMissing.length > 0) {
    const out = stabilizedMissing
      .map((x: any) => String(x || '').trim())
      .filter(Boolean)
      .map((x: string) => (x.startsWith('no_') ? x.slice(3) : x));
    return Array.from(new Set(out.map((x) => x.toLowerCase())));
  }

  // Prefer structured PPE items if present.
  const items = attrs?.ppe_items;
  if (Array.isArray(items) && items.length > 0) {
    const out: string[] = [];
    for (const it of items) {
      if (!it) continue;
      const status = String((it as any).status || '').toLowerCase();
      const label = String((it as any).label || '').trim();
      if (label && (!status || status === 'missing')) out.push(label);
    }
    const uniq = Array.from(new Set(out.map((x) => x.toLowerCase()))).map((x) => x);
    return uniq;
  }

  // Fallback: violations like ["no_helmet","no_vest"].
  const v = attrs?.violations;
  if (Array.isArray(v) && v.length > 0) {
    const out: string[] = [];
    for (const raw of v) {
      const s = String(raw || '').trim();
      if (!s) continue;
      out.push(s.startsWith('no_') ? s.slice(3) : s);
    }
    const uniq = Array.from(new Set(out.map((x) => x.toLowerCase()))).map((x) => x);
    return uniq;
  }

  return [];
};

const _ppeStabilizedStateFromIncident = (inc: Incident | null | undefined): Record<string, string> | null => {
  if (!inc || (inc.incident_type || inc.title) !== 'ppe_violation') return null;
  const attrs = (inc.metrics as any)?.attributes || {};
  const epic27 = (inc as any)?.epic27 || {};
  const state = attrs?.ppe_items_state || epic27?.ppe_items_state;
  return state && typeof state === 'object' ? state : null;
};

const _ppeSnapshotItemsFromIncident = (inc: Incident | null | undefined): Array<{ label: string; status: string }> => {
  if (!inc || (inc.incident_type || inc.title) !== 'ppe_violation') return [];
  const attrs = (inc.metrics as any)?.attributes || {};
  const explicitItems = attrs?.ppe_items_snapshot || attrs?.ppe_items;
  if (Array.isArray(explicitItems) && explicitItems.length > 0) {
    return explicitItems
      .map((it: any) => ({
        label: String(it?.label || '').trim(),
        status: String(it?.status || '').trim().toLowerCase(),
      }))
      .filter((it: any) => it.label && it.status);
  }
  const snapshotState = attrs?.ppe_items_snapshot_state;
  if (snapshotState && typeof snapshotState === 'object') {
    return Object.entries(snapshotState)
      .map(([label, status]) => ({
        label: String(label || '').trim(),
        status: String(status || '').trim().toLowerCase(),
      }))
      .filter((it) => it.label && it.status);
  }
  return [];
};

const _ppeItemsSummary = (items: Array<{ label: string; status: string }> | Record<string, string> | null | undefined): string => {
  if (!items) return '';
  const pairs = Array.isArray(items)
    ? items
    : Object.entries(items).map(([label, status]) => ({ label, status: String(status || '') }));
  return pairs
    .map((it) => `${_titleCase(String(it.label || ''))}: ${String(it.status || '').toLowerCase()}`)
    .filter((x) => !!x)
    .join(', ');
};

const _findingForIncident = (inc: Incident | null | undefined): { kind: 'none' | 'chip' | 'text'; label?: string; chipColor?: any; chipVariant?: any } => {
  if (!inc) return { kind: 'none' };
  const incidentType = String(inc.incident_type || inc.title || '').trim();
  const metrics: any = inc.metrics || {};

  // PPE: show missing items
  const missing = _ppeMissingItemsFromIncident(inc);
  if (missing.length > 0) {
    const label = missing.map(_titleCase).join(', ');
    return { kind: 'chip', label: `Missing items: ${label}`, chipColor: 'warning', chipVariant: 'outlined' };
  }

  // Zone intrusion / zone breach: show a human-friendly finding (not '-')
  if (incidentType === 'zone_breach' || incidentType === 'zone_violation' || incidentType === 'zone_intrusion') {
    const subject = String(metrics?.attributes?.subject || metrics?.subject || 'Unauthorized entry').trim() || 'Unauthorized entry';
    return { kind: 'chip', label: _titleCase(subject), chipColor: 'info', chipVariant: 'outlined' };
  }

  // Dwell / loiter: show threshold context if present
  if (incidentType === 'dwell_violation') {
    const d = Number(metrics?.duration_seconds ?? metrics?.attributes?.duration_seconds);
    const t = Number(metrics?.threshold_seconds ?? metrics?.attributes?.threshold_seconds);
    if (Number.isFinite(d) && Number.isFinite(t)) {
      return { kind: 'text', label: `Dwell ${_formatDuration(d)} > ${_formatDuration(t)}` };
    }
    return { kind: 'text', label: 'Dwell limit exceeded' };
  }
  if (incidentType === 'loitering') {
    const d = Number(metrics?.duration_seconds ?? metrics?.attributes?.duration_seconds);
    const t = Number(metrics?.threshold_seconds ?? metrics?.attributes?.threshold_seconds);
    if (Number.isFinite(d) && Number.isFinite(t)) {
      return { kind: 'text', label: `Loitering ${_formatDuration(d)} > ${_formatDuration(t)}` };
    }
    return { kind: 'text', label: 'Loitering detected' };
  }

  // Line crossing: show line name/direction if available
  if (incidentType === 'line_crossing') {
    const lineName = String(metrics?.line_name || metrics?.attributes?.line_name || '').trim();
    const dir = String(metrics?.direction || metrics?.attributes?.direction || '').trim();
    const base = lineName ? `Crossed ${lineName}` : 'Line crossing';
    return { kind: 'text', label: dir ? `${base} (${dir})` : base };
  }

  // Occupancy: show count context if present
  if (incidentType === 'occupancy') {
    const c = Number(metrics?.count ?? metrics?.current_count ?? metrics?.attributes?.count ?? metrics?.attributes?.current_count);
    const m = Number(metrics?.max_occupancy ?? metrics?.max_count ?? metrics?.attributes?.max_occupancy ?? metrics?.attributes?.max_count);
    if (Number.isFinite(c) && Number.isFinite(m)) {
      return { kind: 'text', label: `Occupancy ${c} > ${m}` };
    }
    return { kind: 'text', label: 'Occupancy exceeded' };
  }

  // Region entrance/exit
  if (incidentType === 'region_entrance' || incidentType === 'region_exiting') {
    const fromZ = String(metrics?.from_zone || metrics?.attributes?.from_zone || '').trim();
    const toZ = String(metrics?.to_zone || metrics?.attributes?.to_zone || '').trim();
    if (fromZ || toZ) return { kind: 'text', label: `${fromZ || '—'} → ${toZ || '—'}` };
    return { kind: 'text', label: incidentType === 'region_entrance' ? 'Region entered' : 'Region exited' };
  }

  return { kind: 'none' };
};

const _pickTopQueueIncident = (opsQueues: any): Incident | null => {
  const q = opsQueues?.queues || {};
  const order = ['escalations', 'overdue', 'unassigned_high_risk', 'my_queue'] as const;
  for (const key of order) {
    const items = Array.isArray(q?.[key]?.items) ? q[key].items : [];
    if (items.length > 0) return items[0] as Incident;
  }
  return null;
};

const _formatDuration = (seconds?: number | null): string => {
  if (seconds == null || !Number.isFinite(seconds)) return '—';
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  const mm = m % 60;
  const ss = s % 60;
  if (h > 0) return `${h}h ${mm}m`;
  if (m > 0) return `${m}m ${ss}s`;
  return `${ss}s`;
};

// Odoo state color mapping
const getOdooStateColor = (state: string | undefined) => {
  switch (state) {
    case 'new': return 'info';
    case 'assigned': return 'primary';
    case 'in_progress': return 'warning';
    case 'resolved': return 'success';
    case 'closed': return 'default';
    default: return 'default';
  }
};

type WorkOrderUiState = 'create' | 'linked' | 'closed';

const _isClosedInOdoo = (odooStateRaw: any): boolean => {
  const s = String(odooStateRaw || '').trim().toLowerCase();
  // safety.incident uses resolved/closed; fallback models may use Done/Closed stage names.
  return (
    s === 'resolved' ||
    s === 'closed' ||
    s === 'done' ||
    s.includes('resolved') ||
    s.includes('closed') ||
    s.includes('done')
  );
};

const _workOrderUiState = (row: { odoo_ticket_id?: any; odoo_ticket_state?: any } | null | undefined): WorkOrderUiState => {
  if (!row?.odoo_ticket_id) return 'create';
  return _isClosedInOdoo(row.odoo_ticket_state) ? 'closed' : 'linked';
};

const _workOrderLabel = (id: any): string => {
  const n = Number(id);
  if (!Number.isFinite(n) || n <= 0) return String(id || '').trim() || 'Work Order';
  return `#OD-${n}`;
};

const _renderWorkOrderCell = (
  row: any,
  opts: {
    onCreate: (incidentId: string) => void;
    onOpen: (url: string | undefined) => void;
  }
) => {
  const uiState = _workOrderUiState(row);
  const url = row?.odoo_ticket_url as string | undefined;
  const incidentId = String(row?.incident_id || '').trim();

  if (uiState === 'create') {
    return (
      <Chip
        label="Create"
        size="small"
        color="primary"
        variant="outlined"
        onClick={(e) => {
          e.stopPropagation();
          if (incidentId) opts.onCreate(incidentId);
        }}
        sx={{ cursor: 'pointer' }}
      />
    );
  }

  if (uiState === 'closed') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Chip
          icon={<CheckIcon sx={{ fontSize: 16 }} />}
          label="Closed in Odoo"
          size="small"
          color="success"
          variant="outlined"
          onClick={(e) => {
            e.stopPropagation();
            opts.onOpen(url);
          }}
          sx={{ cursor: url ? 'pointer' : 'default' }}
        />
        <Typography variant="caption" color="textSecondary">
          {_workOrderLabel(row?.odoo_ticket_id)}
        </Typography>
        <Typography variant="caption" color="textSecondary" sx={{ opacity: 0.8 }}>
          Linked to maintenance workflow
        </Typography>
      </Box>
    );
  }

  // linked
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Chip
        icon={<OdooIcon sx={{ fontSize: 16 }} />}
        label={_workOrderLabel(row?.odoo_ticket_id)}
        size="small"
        color={getOdooStateColor(row?.odoo_ticket_state) as any}
        onClick={(e) => {
          e.stopPropagation();
          opts.onOpen(url);
        }}
        sx={{ cursor: url ? 'pointer' : 'default' }}
      />
      <Typography variant="caption" color="textSecondary" sx={{ opacity: 0.8 }}>
        Linked to maintenance workflow
      </Typography>
    </Box>
  );
};

type OperationalStatus =
  | 'Needs review'
  | 'Assigned'
  | 'Needs action'
  | 'Resolved'
  | 'Suppressed'
  | 'Unmapped';

const _normalizeOdooState = (s: any): string => {
  return String(s || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
};

const deriveOperationalStatus = (row: { odoo_ticket_id?: any; odoo_ticket_state?: any } | null | undefined): OperationalStatus => {
  if (!row) return 'Unmapped';
  const hasTicket = Boolean(row.odoo_ticket_id);
  if (!hasTicket) return 'Needs review';

  const st = _normalizeOdooState(row.odoo_ticket_state);

  // safety.incident custom module uses these exact values.
  if (st === 'new') return 'Needs review';
  if (st === 'assigned') return 'Assigned';
  if (st === 'in_progress') return 'Needs action';
  if (st === 'resolved' || st === 'closed') return 'Resolved';

  // Fallback heuristics for helpdesk.ticket / project.task stage names.
  // Keep conservative: only map when we’re confident; otherwise show Unmapped.
  if (st.includes('triage') || st.includes('ack')) return 'Assigned';
  if (st.includes('assign') || st.includes('scheduled') || st.includes('dispatch') || st.includes('in_progress') || st.includes('inprogress')) return 'Needs action';
  if (st.includes('resolve') || st.includes('closed') || st.includes('done')) return 'Resolved';

  return 'Unmapped';
};

const hasRecordedReviewOutcome = (row: Record<string, any> | null | undefined): boolean => {
  if (!row) return false;
  return Boolean(
    String(
      row.review_outcome
      || row?.safety_case?.review?.review_outcome
      || '',
    ).trim(),
  );
};

const getOperationalStatusColor = (s: OperationalStatus) => {
  switch (s) {
    case 'Needs review':
      return 'info';
    case 'Assigned':
      return 'primary';
    case 'Needs action':
      return 'warning';
    case 'Resolved':
      return 'success';
    case 'Suppressed':
      return 'default';
    default:
      return 'default';
  }
};

const _opsStateLabel = (v: any): string => {
  return formatFollowUpStateLabel(String(v || ''));
};

const _opsStateColor = (v: any): 'info' | 'primary' | 'warning' | 'success' | 'default' => {
  const s = String(v || '').trim().toLowerCase();
  if (s === 'new') return 'info';
  if (s === 'acknowledged') return 'primary';
  if (s === 'in_progress') return 'warning';
  if (s === 'resolved') return 'success';
  if (s === 'closed') return 'default';
  return 'info';
};

/** Evidence file GET requires JWT; native <img>/<video src> cannot send Authorization (only cookies). */
const IncidentEvidenceAttachmentPreview: React.FC<{
  incidentId: string;
  filename: string;
  variant: 'image' | 'video';
  fetchEvidenceFileBlob: (incidentId: string, filename: string) => Promise<Blob>;
}> = ({ incidentId, filename, variant, fetchEvidenceFileBlob }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    setObjectUrl(null);
    if (urlRef.current) {
      try {
        URL.revokeObjectURL(urlRef.current);
      } catch {
        // ignore
      }
      urlRef.current = null;
    }

    (async () => {
      try {
        const blob = await fetchEvidenceFileBlob(incidentId, filename);
        if (cancelled) return;
        const u = URL.createObjectURL(blob);
        urlRef.current = u;
        setObjectUrl(u);
      } catch {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (urlRef.current) {
        try {
          URL.revokeObjectURL(urlRef.current);
        } catch {
          // ignore
        }
        urlRef.current = null;
      }
    };
  }, [incidentId, filename, fetchEvidenceFileBlob]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 1, minHeight: 120 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (loadError || !objectUrl) {
    return (
      <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
        Preview failed (check auth). Use Open to download.
      </Typography>
    );
  }

  const mediaSx = {
    mt: 1,
    width: '100%',
    maxHeight: 260,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.default',
    ...(variant === 'image' ? { objectFit: 'contain' as const } : {}),
  };

  if (variant === 'image') {
    return <Box component="img" alt={filename} src={objectUrl} sx={mediaSx} />;
  }
  return (
    <Box sx={{ mt: 1 }}>
      <Box component="video" controls src={objectUrl} sx={mediaSx} />
    </Box>
  );
};

function PolicyNotificationStatusChip({ route }: { route: PolicyNotificationRoute }) {
  switch (route.state) {
    case 'delivered':
      return <Chip icon={<CheckIcon />} label="Delivered" size="small" color="success" />;
    case 'failed':
      return (
        <Tooltip title={route.error ? String(route.error) : 'Delivery failed'}>
          <Chip
            icon={<FailIcon />}
            label={route.error ? `${String(route.error).slice(0, 72)}${String(route.error).length > 72 ? '…' : ''}` : 'Failed'}
            size="small"
            color="error"
          />
        </Tooltip>
      );
    case 'pending':
      return <Chip label="Pending" size="small" color="warning" variant="outlined" />;
    case 'disabled':
      return <Chip label="Disabled on policy" size="small" variant="outlined" />;
    case 'not_listed':
      return (
        <Tooltip title="This action type is not present on the policy’s actions[] for this incident.">
          <Chip label="Not on policy" size="small" variant="outlined" />
        </Tooltip>
      );
    case 'no_snapshot':
      return (
        <Tooltip title="No envelope actions[] (e.g. missing policy_id when the incident was created, or an older record). SMS/email/Genetec won’t run without a snapshot.">
          <Chip label="No snapshot" size="small" variant="outlined" />
        </Tooltip>
      );
    default:
      return <Chip label="Unknown" size="small" variant="outlined" />;
  }
}

type IncidentsDialogStackProps = {
  thresholdDialogOpen: boolean;
  closeDialogOpen: boolean;
  closeReviewOutcomePreset: IncidentReviewOutcome | null;
  resolveIncidentId?: string;
  groupedEvidenceResolvePresentation?: ReturnType<typeof groupedEvidenceReviewResolvePresentation>;
  groupPatternDialogOpen: boolean;
  selectedIncident: Incident | null;
  observationDispositionMode?: boolean;
  closeReason: string;
  drawerBusy: string | null;
  canManageIncidentWorkflow: boolean;
  canAdjustIncidentThreshold: boolean;
  scopedSiteId: string;
  scopedOrgId: string;
  groupPatternDialogMode: IncidentGroupPatternActionMode;
  groupPatternDialogGroup: IncidentGroup | null;
  timeRange: IncidentTimeRange;
  canManageGroupPatterns: boolean;
  onCloseThreshold: () => void;
  onCloseResolve: () => void;
  onCloseGroupPattern: () => void;
  onApplyThreshold: (adjustment: Record<string, number>, autoDeploy: boolean) => Promise<void>;
  onOpenPolicyEditor: () => void;
  onResolve: (params: {
    reason: string;
    reviewOutcome: any;
    note: string;
    tags: string[];
  }) => Promise<void>;
  onGroupPatternCompleted: () => void;
  patternVolumeCount?: number;
  zoneDisplayLabel?: string;
  cameraDisplayLabel?: string;
  onViewAffectedOccurrences?: (scope: PatternActionViewScope) => void;
  onRunPatternActionInBackground?: (payload: {
    groupKey: string;
    body: IncidentGroupActionRequest;
    mode: IncidentGroupPatternActionMode;
    matchedCount: number;
    patternLabel: string;
  }) => void;
};

const OBSERVATION_BLOCKED_REVIEW_OUTCOMES: IncidentReviewOutcome[] = [
  'true_event',
  'false_positive',
  'duplicate',
];

const IncidentsDialogStack: React.FC<IncidentsDialogStackProps> = ({
  thresholdDialogOpen,
  closeDialogOpen,
  closeReviewOutcomePreset,
  resolveIncidentId,
  groupedEvidenceResolvePresentation,
  groupPatternDialogOpen,
  selectedIncident,
  observationDispositionMode = false,
  closeReason,
  drawerBusy,
  canManageIncidentWorkflow,
  canAdjustIncidentThreshold,
  scopedSiteId,
  scopedOrgId,
  groupPatternDialogMode,
  groupPatternDialogGroup,
  timeRange,
  canManageGroupPatterns,
  onCloseThreshold,
  onCloseResolve,
  onCloseGroupPattern,
  onApplyThreshold,
  onOpenPolicyEditor,
  onResolve,
  onGroupPatternCompleted,
  patternVolumeCount = 0,
  zoneDisplayLabel,
  cameraDisplayLabel,
  onViewAffectedOccurrences,
  onRunPatternActionInBackground,
}) => {
  const existingReviewOutcome = String(
    (selectedIncident as any)?.review_outcome
    || (selectedIncident as any)?.safety_case?.review?.review_outcome
    || '',
  ).trim() || null;
  const existingReviewOutcomeSource = String(
    (selectedIncident as any)?.review_outcome_source
    || (selectedIncident as any)?.safety_case?.review?.review_outcome_source
    || '',
  ).trim() || null;
  const evidenceAttachmentCount = Array.isArray((selectedIncident as any)?.evidence?.attachments)
    ? (selectedIncident as any).evidence.attachments.length
    : 0;
  const observationEvidenceReadinessState = String(
    (selectedIncident as any)?.safety_case?.evidence?.readiness_state || '',
  ).trim().toUpperCase();
  const hasUsableObservationEvidence = Boolean(
    evidenceAttachmentCount > 0
    || String((selectedIncident as any)?.snapshot_status || '').trim().toUpperCase() === 'AVAILABLE'
    || String((selectedIncident as any)?.clip_status || '').trim().toUpperCase() === 'AVAILABLE'
    || (selectedIncident as any)?.snapshot_uri
    || (selectedIncident as any)?.clip_uri,
  );
  const observationReviewOutcomeBlockReason = observationDispositionMode
    ? !hasUsableObservationEvidence
      ? 'No usable snapshot or clip is available for this observation yet. Record Needs more review until evidence is available.'
      : observationEvidenceReadinessState === 'PENDING'
        ? 'Evidence for this observation is still being prepared. Record Needs more review until a usable snapshot or clip is available.'
        : observationEvidenceReadinessState === 'FAILED'
          ? 'Evidence for this observation failed to load. Record Needs more review until a usable snapshot or clip is available.'
          : observationEvidenceReadinessState === 'DEGRADED'
            ? 'Evidence for this observation is degraded. Record Needs more review until a usable snapshot or clip is available.'
            : ''
    : '';
  return (
  <>
    <IncidentThresholdDialog
      open={thresholdDialogOpen}
      onClose={onCloseThreshold}
      selectedIncident={selectedIncident}
      drawerBusy={drawerBusy}
      canAdjust={canAdjustIncidentThreshold}
      scopedSiteId={scopedSiteId}
      scopedOrgId={scopedOrgId}
      onApply={onApplyThreshold}
      onOpenPolicyEditor={onOpenPolicyEditor}
    />
    <IncidentResolveDialog
      open={closeDialogOpen}
      onClose={onCloseResolve}
      incidentId={resolveIncidentId || selectedIncident?.incident_id}
      initialReason={closeReason}
      initialReviewOutcome={
        closeReviewOutcomePreset
        || (selectedIncident as any)?.review_outcome
        || (selectedIncident as any)?.safety_case?.review?.review_outcome
        || null
      }
      existingReviewOutcome={existingReviewOutcome}
      existingReviewOutcomeSource={existingReviewOutcomeSource}
      blockedReviewOutcomes={observationReviewOutcomeBlockReason ? OBSERVATION_BLOCKED_REVIEW_OUTCOMES : []}
      reviewOutcomeBlockReason={observationReviewOutcomeBlockReason || null}
      drawerBusy={drawerBusy}
      canManage={canManageIncidentWorkflow}
      presentation={groupedEvidenceResolvePresentation || undefined}
      onResolve={onResolve}
    />
    <IncidentGroupPatternActionDialog
      open={groupPatternDialogOpen}
      mode={groupPatternDialogMode}
      group={groupPatternDialogGroup}
      timeRange={timeRange}
      canManage={canManageGroupPatterns}
      initialOwner={
        groupPatternDialogMode === 'assign' ? readPatternOwnerLabel(selectedIncident) : undefined
      }
      initialAssignmentTeam={
        groupPatternDialogMode === 'assign' ? readPatternOwnerTeamLabel(selectedIncident) : undefined
      }
      patternVolumeCount={patternVolumeCount}
      zoneDisplayLabel={zoneDisplayLabel}
      cameraDisplayLabel={cameraDisplayLabel}
      onViewAffectedOccurrences={onViewAffectedOccurrences}
      onClose={onCloseGroupPattern}
      onCompleted={onGroupPatternCompleted}
      onRunInBackground={onRunPatternActionInBackground}
      runExecuteInBackground={Boolean(onRunPatternActionInBackground)}
    />
  </>
  );
};

const Incidents: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const reviewMode = String(searchParams.get('review') || '').trim() === '1'
    && (
      Boolean(String(searchParams.get('incident_id') || '').trim())
      || Boolean(String(searchParams.get('workflow_id') || '').trim())
    );
  const reviewExactMode = String(searchParams.get('exact_incident') || '').trim() === '1';
  const workflowIdFromUrl = String(searchParams.get('workflow_id') || '').trim() || null;
  const workflowProblemContext = useMemo(() => resolveIncidentWorkflowProblemContext({
    workflowId: searchParams.get('workflow_id'),
    groupKey: searchParams.get('group_key'),
    conditionId: searchParams.get('condition_id'),
    cameraId: searchParams.get('camera_id'),
    zoneId: searchParams.get('zone_id'),
    incidentType: searchParams.get('incident_type'),
  }), [searchParams]);
  const exactReviewIncidentId = reviewExactMode
    ? String(searchParams.get('incident_id') || '').trim()
    : '';

  // Top-level Incidents should open the broader site view, not auto-narrow to the
  // operator queue preset. Explicit queue/device/camera deep links still apply later
  // via URL orchestration.
  const _initialPreset: OperatorPreset = '';

  const {
    tabValue,
    setTabValue,
    tableMode,
    setTableMode: setTableModeState,
    operatorPreset,
    setOperatorPreset,
    filterIncidentType,
    setFilterIncidentType,
    filterSeverity,
    setFilterSeverity,
    filterZoneId,
    setFilterZoneId,
    filterDeviceId,
    setFilterDeviceId,
    filterCameraId,
    setFilterCameraId,
    filterIncidentStatus,
    setFilterIncidentStatus,
    filterWorkflowStatus,
    setFilterWorkflowStatus,
    filterOpsQueue,
    setFilterOpsQueue,
    pendingQueueAutoOpen,
    setPendingQueueAutoOpen,
    filterOpsAssignee,
    setFilterOpsAssignee,
    filterMinAgeMinutes,
    setFilterMinAgeMinutes,
    searchIncidentId,
    setSearchIncidentId,
    filterHeatmapHour,
    setFilterHeatmapHour,
    filterPolicyPack,
    setFilterPolicyPack,
    advancedTableFilterCount,
    sortBySeverity,
    setSortBySeverity,
    sortModel,
    setSortModel,
    _defaultSortModel,
    _applyPreset,
  } = useIncidentsFilters(_initialPreset);

  const persistTableMode = useCallback((mode: IncidentTableMode) => {
    setTableModeState(mode);
    const current = readIncidentTableModeParam(searchParams);
    const hasCamelCase = Boolean(searchParams.get('tableMode'));
    if (current === mode && !hasCamelCase) return;
    const next = new URLSearchParams(searchParams);
    next.delete('tableMode');
    next.set('table_mode', mode);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, setTableModeState]);
  const setTableMode = persistTableMode;

  useEffect(() => {
    const explicit = readIncidentTableModeParam(searchParams);
    if (explicit) {
      setTableModeState(explicit);
      if (searchParams.get('tableMode') && !searchParams.get('table_mode')) {
        const next = new URLSearchParams(searchParams);
        next.delete('tableMode');
        next.set('table_mode', explicit);
        setSearchParams(next, { replace: true });
      }
      return;
    }
    const implied = impliedIncidentTableMode(searchParams);
    setTableModeState(implied);
    const next = new URLSearchParams(searchParams);
    next.delete('tableMode');
    next.set('table_mode', implied);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, setTableModeState]);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const {
    viewDialogOpen,
    setViewDialogOpen,
    selectedIncident,
    setSelectedIncident,
    selectedGroupContext,
    setSelectedGroupContext,
    viewDialogTab,
    setViewDialogTab,
    drawerAdvancedOpen,
    setDrawerAdvancedOpen,
  } = useIncidentDrawerState();
  const reviewSurfaceMode = React.useMemo(
    () => resolveIncidentReviewSurfaceMode({
      entryMode: reviewExactMode
        ? 'observation_disposition'
        : workflowIdFromUrl
          ? 'pattern_case'
          : null,
      reviewExactMode,
      selectedIncident,
      selectedGroupContext,
    }),
    [reviewExactMode, selectedGroupContext, selectedIncident, workflowIdFromUrl],
  );
  const patternCaseReviewMode = isPatternCaseReviewMode(reviewSurfaceMode);
  const observationDispositionMode = isObservationDispositionMode(reviewSurfaceMode);
  const [error, setError] = useState<string | null>(null);
  const [patternActionSnack, setPatternActionSnack] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });
  const [heatmapViewFeedback, setHeatmapViewFeedback] = useState<string | null>(null);
  const [heatmapPinnedIncidentId, setHeatmapPinnedIncidentId] = useState<string | null>(null);
  const [groupPatternDialogOpen, setGroupPatternDialogOpen] = useState(false);
  const [groupPatternDialogMode, setGroupPatternDialogMode] = useState<IncidentGroupPatternActionMode>('close');
  const [groupPatternDialogGroup, setGroupPatternDialogGroup] = useState<IncidentGroup | null>(null);
  const [groupPatternActionBusy, setGroupPatternActionBusy] = useState(false);
  const [groupedCaseStateFilter, setGroupedCaseStateFilter] = useState<'all' | 'awaiting_review' | 'monitoring'>('all');
  const [groupedReviewDigestDismissedToken, setGroupedReviewDigestDismissedToken] = useState(() => {
    try {
      return window.localStorage.getItem(GROUPED_REVIEW_DIGEST_DISMISS_KEY) || '';
    } catch {
      return '';
    }
  });
  const [drawerIncidentLoading, setDrawerIncidentLoading] = useState(false);
  const [opsStateResult, setOpsStateResult] = useState(() => deriveOpsState({}));
  const [scopeDialogOpen, setScopeDialogOpen] = useState(false);
  const [scopeOrgDraft, setScopeOrgDraft] = useState<string>('');
  const [scopeSiteDraft, setScopeSiteDraft] = useState<string>('');
  const [, setScopeNonce] = useState(0);

  // Ops Copilot: suggested remediation actions (fleet-scoped)
  const [_opsRecoveryBusy, setOpsRecoveryBusy] = useState(false);
  const [opsRecoveryResult, setOpsRecoveryResult] = useState<any | null>(null);
  const [opsRecoveryError, setOpsRecoveryError] = useState<string | null>(null);

  // Incident Command Center: runbook suggestions from taxonomy
  const { effectiveUser: user } = useAuth();
  const operatorActorId = useMemo(() => resolveOperatorActorId(user), [user]);
  const capabilityProfile = useMemo(() => getWarehouseCapabilityProfile(user), [user]);
  const incidentsAdvancedEligible = useMemo(
    () => canUseAdvancedIncidentsUi(capabilityProfile.capabilities),
    [capabilityProfile.capabilities]
  );
  const {
    incidentsUiMode,
    setIncidentsUiModePersist,
  } = useIncidentsUiMode(user?.id, incidentsAdvancedEligible);
  const isAdvancedIncidentsUi = incidentsUiMode === 'advanced';

  const canManageIncidentWorkflow = canManageIncidents(capabilityProfile.capabilities);
  const _canOpenRunbooks = hasAccessCapability(capabilityProfile.capabilities, 'audit:read');
  const canUseOpsAutomation = canUseIncidentOpsAutomation(capabilityProfile.capabilities);
  const _canCreateIncident = canManageIncidents(capabilityProfile.capabilities);
  const canOpenArtifactEntry = canOpenIncidentsArtifactEntry(capabilityProfile.capabilities);
  const canCommentOnIncident = canManageIncidentWorkflow;
  const canOverrideIncidentSeverity = canManageIncidentWorkflow;
  const canManageIncidentSuppressions = canManageIncidentWorkflow;
  const canExportIncidentEvidence = capabilityProfile.canReadIncidents;
  const canAdjustIncidentThreshold =
    capabilityProfile.hasAdminAccess || hasAccessCapability(capabilityProfile.capabilities, 'policies:write');
  const canOpenAuditTrail = hasAccessCapability(capabilityProfile.capabilities, 'audit:read');
  const canOpenCopilot = hasAccessCapability(capabilityProfile.capabilities, 'audit:read');
  const canRunIncidentRunbooks = canUseOpsAutomation;
  const [incidentRunbookBusy, setIncidentRunbookBusy] = useState<string | null>(null);
  const commentInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const {
    runbookTemplates,
    incidentActivityItems,
    setIncidentActivityItems,
    incidentActivityLoading,
    incidentActivityError,
    incidentComments,
    setIncidentComments,
    incidentCommentsLoading,
    incidentCommentsError,
    setIncidentCommentsError,
    incidentSafetyEpisodes,
    incidentSafetyEpisodesLoading,
    incidentSafetyEpisodesError,
    incidentSafetyEpisodesAvailable,
  } = useIncidentDrawerData({
    viewDialogOpen,
    viewDialogTab,
    setViewDialogTab,
    setDrawerAdvancedOpen,
    selectedIncident,
  });
  // Sticky context: remember the last focused incident (and its camera/device) so Live Ops can resume.
  useEffect(() => {
    try {
      if (!viewDialogOpen) return;
      const incidentId = String(selectedIncident?.incident_id || '').trim();
      if (!incidentId) return;
      const cameraId = String((selectedIncident as any)?.camera_id || '').trim();
      const deviceId = String((selectedIncident as any)?.device_id || '').trim();
      const siteId = _scopedSiteId();
      const prev = readNavContext();
      writeNavContext(
        mergeNavContext(prev, {
          incident_id: incidentId,
          ...(cameraId ? { camera_id: cameraId } : {}),
          ...(deviceId ? { device_id: deviceId } : {}),
          ...(siteId ? { site_id: siteId } : {}),
        })
      );
    } catch {
      // best-effort
    }
  }, [viewDialogOpen, selectedIncident?.incident_id]);

  const _runTicketingRecovery = useCallback(async () => {
    setOpsRecoveryBusy(true);
    setOpsRecoveryResult(null);
    setOpsRecoveryError(null);
    try {
      const health = (await api.get('/api/v1/health/incident_e2e')).data;
      const retry = (await api.post('/api/v1/pipeline/ticketing/retry', { limit: 50 })).data;
      setOpsRecoveryResult({ health, retry });
    } catch (e: any) {
      setOpsRecoveryError(e?.response?.data?.error || e?.message || 'ticketing_recovery_failed');
    } finally {
      setOpsRecoveryBusy(false);
    }
  }, []);

  const clearOpsRecovery = useCallback(() => {
    setOpsRecoveryError(null);
    setOpsRecoveryResult(null);
  }, []);

  // IMPORTANT: do NOT memoize this only on scopeNonce — localStorage/JWT-derived scope can
  // become available after login without touching scopeNonce. We want camera/device options
  // to refresh when scope becomes known.
  const activeScope = (() => {
    try {
      // ensureUiScope may persist JWT-derived scope into localStorage (best-effort)
      return ensureUiScope();
    } catch {
      return { orgId: '', siteId: '', source: 'none' as const };
    }
  })();
  const scopedSiteId = String(activeScope.siteId || '').trim();

  useEffect(() => {
    if (!scopeDialogOpen) return;
    try {
      const s = ensureUiScope();
      setScopeOrgDraft(String(s.orgId || '').trim());
      setScopeSiteDraft(String(s.siteId || '').trim());
    } catch {
      setScopeOrgDraft('');
      setScopeSiteDraft('');
    }
  }, [scopeDialogOpen]);

  const handleCopyOpsContext = useCallback(() => {
    const s = buildContextBundle({
      orgId: activeScope?.orgId,
      siteId: scopedSiteId,
      page: 'incidents',
      state: opsStateResult.state,
      reasons: opsStateResult.reasons,
      userRole: user?.role,
    });
    navigator.clipboard.writeText(s).catch(() => {});
  }, [activeScope?.orgId, scopedSiteId, opsStateResult.state, opsStateResult.reasons, user?.role]);

  useEffect(() => {
    const t = window.setInterval(() => {
      try {
        setOpsStateResult(deriveOpsState({}));
      } catch {
        // ignore
      }
    }, 3000);
    return () => {
      try {
        window.clearInterval(t);
      } catch {
        // ignore
      }
    };
  }, []);

  // Time filter (drives KPIs + grouped + raw list)
  const {
    timePreset,
    setTimePreset,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    timeRange,
  } = useIncidentsTimeRange();

  const [tableFiltersOpen, setTableFiltersOpen] = useState(false);
  const queueBlockRef = useRef<HTMLDivElement | null>(null);
  const insightBandRef = useRef<HTMLDivElement | null>(null);
  const hotspotsCardRef = useRef<HTMLDivElement | null>(null);

  const _severityRank = (s: any): number => {
    switch (String(s || '').toLowerCase()) {
      case 'critical':
        return 4;
      case 'high':
        return 3;
      case 'medium':
        return 2;
      case 'low':
        return 1;
      default:
        return 0;
    }
  };

  const _severityChipSx = (severity: string) => {
    const s = String(severity || '').toLowerCase();
    if (s === 'critical') return { bgcolor: 'error.dark', color: 'common.white', fontWeight: 800 };
    if (s === 'high') return { bgcolor: 'error.main', color: 'common.white', fontWeight: 800 };
    if (s === 'medium') return { bgcolor: 'warning.main', color: 'common.black', fontWeight: 800 };
    if (s === 'low') return { bgcolor: 'grey.300', color: 'common.black', fontWeight: 700 };
    return { fontWeight: 700 };
  };

  const _severityIcon = (severity: string) => {
    const s = String(severity || '').toLowerCase();
    if (s === 'critical' || s === 'high') return <ErrorOutlineIcon sx={{ fontSize: 16 }} />;
    if (s === 'medium') return <ReportProblemOutlinedIcon sx={{ fontSize: 16 }} />;
    return undefined;
  };

  // Reference data for human-friendly labels
  const {
    allCameras,
    allZones,
    allPolicies,
    zoneNameById,
    deviceNameById,
    cameraNameById,
    locationLookups,
    zoneOptions,
    deviceOptions,
    cameraOptions,
  } = useIncidentsReferenceData({
    scopedSiteId,
    isSelectableDeviceForIncidentsFilter: _isSelectableDeviceForIncidentsFilter,
    deviceFilterLabel: _deviceFilterLabel,
    titleCase: _titleCase,
  });
  const policyTypeByIncidentIdentity = useMemo(() => {
    const byPolicyId = new Map<string, string>();
    const byPolicyName = new Map<string, string>();
    for (const policy of allPolicies || []) {
      const policyType = String(policy?.policy_type || '').trim();
      if (!policyType) continue;
      const policyId = String(policy?.policy_id || policy?.id || '').trim();
      const policyName = String(policy?.name || '').trim().toLowerCase();
      if (policyId) byPolicyId.set(policyId, policyType);
      if (policyName) byPolicyName.set(policyName, policyType);
    }
    return { byPolicyId, byPolicyName };
  }, [allPolicies]);
  const packFilterMaps = useMemo(
    () => ({
      policyById: buildPolicyByIdMap(allPolicies),
      zonesById: buildZonesByIdMap(allZones),
    }),
    [allPolicies, allZones],
  );
  const selectedIncidentControlReviewAlias = useMemo(() => {
    const row = selectedIncident as any;
    const policyId = String(row?.policy_id || row?.triggered_by?.policy_id || '').trim();
    if (!policyId) return '';
    const matchedPolicy = (allPolicies || []).find(
      (policy) => String(policy?.policy_id || policy?.id || '').trim() === policyId,
    );
    return String(readPolicyUiContext(matchedPolicy)?.policy_alias || '').trim();
  }, [allPolicies, selectedIncident]);
  const incidentRowTypeLabel = useCallback(
    (row: Incident | Record<string, unknown> | null | undefined) => {
      const record = (row || {}) as any;
      const zoneId = String(record?.zone_id || '').trim();
      const zoneName = zoneId ? zoneNameById.get(zoneId) : undefined;
      return incidentDisplayTitleForRow(
        row,
        (candidate) => {
          const candidateRecord = (candidate || {}) as any;
          const policyId = String(candidateRecord?.policy_id || candidateRecord?.triggered_by?.policy_id || '').trim();
          if (policyId && policyTypeByIncidentIdentity.byPolicyId.has(policyId)) {
            return policyTypeByIncidentIdentity.byPolicyId.get(policyId);
          }
          const policyName = String(candidateRecord?.policy_name || candidateRecord?.triggered_by?.policy_name || '').trim().toLowerCase();
          if (policyName && policyTypeByIncidentIdentity.byPolicyName.has(policyName)) {
            return policyTypeByIncidentIdentity.byPolicyName.get(policyName);
          }
          return '';
        },
        { zoneName },
      );
    },
    [policyTypeByIncidentIdentity, zoneNameById],
  );
  const {
    latestSnapshotObjectUrl,
    latestSnapshotLoading,
    latestSnapshotError,
    latestVideoObjectUrl,
    latestVideoLoading,
    latestVideoError,
    latestVideoIdentity,
    loadEvidenceMoment,
    downloadBlob,
    fetchLatestSnapshotBlob,
    fetchLatestVideoBlob,
    fetchEvidenceFileBlob,
    _bestSnapshotAttachment,
    _bestVideoAttachment,
    _humanLabelCompact,
    _overlaySnapshotBlob,
    evidencePopover,
    evidencePreviewUrlByIncident,
    evidencePreviewLoadingByIncident,
    evidencePreviewErrorByIncident,
    openEvidencePopover,
    closeEvidencePopover,
  } = useIncidentEvidence({
    allZones,
    selectedIncident,
    // Review page forces the drawer open; evidence blobs must load in that mode too.
    viewDialogOpen: reviewMode ? true : viewDialogOpen,
    findingForIncident: _findingForIncident,
    incidentTypeLabel,
  });

  const [edgeSuppressionsByDevice, setEdgeSuppressionsByDevice] = useState<Record<string, any[]>>({});

  const _loadEdgeSuppressionsForDevices = useCallback(async (deviceIds: string[]) => {
    const uniq = Array.from(new Set((deviceIds || []).map((d) => String(d || '').trim()).filter(Boolean)));
    if (uniq.length === 0) return;
    const limited = uniq.slice(0, 3); // avoid fan-out
    const updates: Record<string, any[]> = {};
    await Promise.all(
      limited.map(async (dev) => {
        try {
          const res = await api.get(`/api/v1/pipeline/devices/${encodeURIComponent(dev)}/edge/suppressions`);
          const items = (res?.data?.suppressions || []) as any[];
          updates[dev] = Array.isArray(items) ? items : [];
        } catch {
          updates[dev] = [];
        }
      }),
    );
    setEdgeSuppressionsByDevice((prev) => ({ ...prev, ...updates }));
  }, []);

  const {
    incidents,
    setIncidents,
    filteredIncidents,
    incidentsTotal,
    kpis,
    opsQueues,
    reviewOutcomeRollup,
    groups,
    loading,
    fetchData,
    clearLoading,
  } = useIncidentsData({
    tabValue,
    tableMode,
    filterIncidentType,
    filterSeverity,
    filterZoneId,
    filterDeviceId,
    filterCameraId,
    filterIncidentStatus,
    filterWorkflowStatus,
    filterOpsQueue,
    filterOpsAssignee,
    operatorActorId,
    timeRange,
    searchIncidentId,
    onLoadError: setError,
    loadEdgeSuppressionsForDevices: _loadEdgeSuppressionsForDevices,
  });

  const {
    newIncident,
    setNewIncident,
    handleCreateIncident,
    handleSyncFromOdoo,
    handleEnsureOdooTicket,
    handleSyncAllFromOdoo: _handleSyncAllFromOdoo,
  } = useIncidentsPageActions({
    fetchData,
    setError,
    setCreateDialogOpen,
  });

  const groupedReviewTargetIncidentId = exactReviewIncidentId
    || resolveRepresentativeIncidentIdForReview(
      selectedIncident as any,
      selectedGroupContext,
    )
    || undefined;
  const {
    drawerBusy,
    suppressionsLoading,
    suppressionsError,
    edgeSuppressions,
    suppressDialogOpen,
    setSuppressDialogOpen,
    closeDialogOpen,
    setCloseDialogOpen,
    closeReviewOutcomePreset,
    setCloseReviewOutcomePreset,
    openResolveDialogWithOutcome,
    closeReason,
    setCloseReason,
    thresholdDialogOpen,
    setThresholdDialogOpen,
    noteDraft,
    setNoteDraft,
    severityDraft,
    setSeverityDraft,
    opsStateDraft,
    setOpsStateDraft,
    opsAssigneeDraft,
    setOpsAssigneeDraft,
    opsTeamDraft,
    setOpsTeamDraft,
    opsSlaTargetDraft,
    setOpsSlaTargetDraft,
    measurementBaselineDraft,
    setMeasurementBaselineDraft,
    measurementFollowUpDraft,
    setMeasurementFollowUpDraft,
    measurementMeasuredAtDraft,
    setMeasurementMeasuredAtDraft,
    measurementOutcomeStatusDraft,
    setMeasurementOutcomeStatusDraft,
    measurementNoteDraft,
    setMeasurementNoteDraft,
    measurementImpactWaivedDraft,
    setMeasurementImpactWaivedDraft,
    correctiveActionTypeDraft,
    setCorrectiveActionTypeDraft,
    correctiveActionPriorityDraft,
    setCorrectiveActionPriorityDraft,
    correctiveActionDueDateDraft,
    setCorrectiveActionDueDateDraft,
    correctiveActionVerificationMethodDraft,
    setCorrectiveActionVerificationMethodDraft,
    handleSaveCorrectiveAction,
    workflowId,
    workflowCorrectiveActions,
    workflowImpactSummary,
    refreshWorkflowCorrectiveSummary,
    handleAddWorkflowEvidenceNote,
    handleUpdateWorkflowActionStatus,
    ppeMissingZoneDiag,
    ppeMissingZoneDiagLoading,
    ppeMissingZoneDiagError,
    refreshSelectedIncident,
    handleAcknowledge,
    handleMarkInReview,
    handleEnsureWorkOrder,
    handleSyncToOdoo,
    handleSaveNote,
    handleAddCommentShortcut,
    handleOverrideSeverity,
    handleSaveOps,
    handleSaveFollowupOwner,
    handleSaveCoverageReviewAttestation,
    handleSaveZoneControlsConfirmation,
    handleSaveMeasurement,
    claimReadinessRefreshKey,
    workflowClaimReadiness,
    handleMarkFalsePositive,
    handleExportEvidence,
    handleOpenThresholdTuning,
    handleOpenPolicyEditor,
    handleCloseIncidentWith,
    handleSuppressSimilarWith,
    handleApplyThresholdAdjustmentWith,
    loadEdgeSuppressions,
    loadPpeMissingZoneDiag,
    matchingSuppressions,
    deriveLifecycle,
    handleClearSuppressionForIncident,
    workflowFeedback,
    clearWorkflowFeedback,
  } = useIncidentDrawerOperations({
    selectedIncident,
    setSelectedIncident,
    setIncidents,
    edgeSuppressionsByDevice,
    setEdgeSuppressionsByDevice,
    loadEdgeSuppressionsForDevices: _loadEdgeSuppressionsForDevices,
    viewDialogOpen,
    setViewDialogTab,
    fetchData,
    setError,
    setIncidentComments,
    setIncidentCommentsError,
    setIncidentActivityItems,
    commentInputRef,
    navigate,
    getScopedOrgId: _scopedOrgId,
    getScopedSiteId: _scopedSiteId,
    fetchEvidenceFileBlob,
    fetchLatestSnapshotBlob,
    fetchLatestVideoBlob,
    downloadBlob,
    bestSnapshotAttachment: _bestSnapshotAttachment,
    deriveOperationalStatus,
    groupedReviewMode: patternCaseReviewMode,
    resolveTargetIncidentId: groupedReviewTargetIncidentId,
    workflowId: String(
      (selectedGroupContext as any)?.group_key
      || (selectedIncident as any)?.group_key
      || (selectedIncident as any)?.safety_case?.workflow_id
      || '',
    ).trim(),
    controlReviewPolicyAlias: selectedIncidentControlReviewAlias,
  });

  const incidentTypeOptions = React.useMemo(() => {
    const set = new Set<string>();
    for (const k of KNOWN_INCIDENT_TYPE_KEYS) set.add(k);
    for (const i of incidents || []) if (i?.incident_type) set.add(i.incident_type);
    for (const g of groups || []) if ((g as any)?.incident_type) set.add(String((g as any).incident_type));
    return Array.from(set)
      .filter(Boolean)
      .sort((a, b) => incidentTypeLabel(a).localeCompare(incidentTypeLabel(b)))
      .map((v) => ({ value: v, label: incidentTypeLabel(v) }));
  }, [incidents, groups]);

  const visibleRows = React.useMemo(() => {
    const want = filterWorkflowStatus;
    const minAge = filterMinAgeMinutes === '' ? 0 : Number(filterMinAgeMinutes) || 0;
    const cutoffTs = minAge > 0 ? Date.now() - minAge * 60_000 : 0;
    const match = (row: any): boolean => {
      if (!want) return true;
      if (want === 'Reviewed') return hasRecordedReviewOutcome(row as Record<string, any>);
      const s = deriveOperationalStatus(row as any);
      if (want === 'Unmapped') return s === 'Unmapped';
      return s === want;
    };
    const matchAge = (row: any): boolean => {
      if (!cutoffTs) return true;
      try {
        const raw = String((row as any)?.created_at || (row as any)?.first_seen || '').trim();
        if (!raw) return true;
        const t = Date.parse(raw);
        if (!Number.isFinite(t)) return true;
        return t <= cutoffTs;
      } catch {
        return true;
      }
    };
    const matchHeatmapUtc = (row: any): boolean => {
      if (filterHeatmapHour == null) return true;
      if (tableMode !== 'raw' || String((row as any)?.episode_id || '').trim()) return true;
      const raw = String((row as any)?.created_at || '').trim();
      if (!raw) return false;
      const t = Date.parse(raw);
      if (!Number.isFinite(t)) return false;
      return new Date(t).getUTCHours() === filterHeatmapHour;
    };
    const wantPack = String(filterPolicyPack || '').trim();
    const matchPack = (row: any): boolean => {
      if (!wantPack) return true;
      const pk = resolvePackKeyForGroup(row, packFilterMaps.policyById, { zonesById: packFilterMaps.zonesById });
      return pk === wantPack;
    };
    if (tableMode === 'grouped') return (groups || []).filter((r) => match(r) && matchAge(r) && matchPack(r));
    return (filteredIncidents || []).filter((r) => match(r) && matchAge(r) && matchHeatmapUtc(r) && matchPack(r));
  }, [
    tableMode,
    groups,
    filteredIncidents,
    filterWorkflowStatus,
    filterMinAgeMinutes,
    filterHeatmapHour,
    filterPolicyPack,
    packFilterMaps,
  ]);

  const displayedRows = React.useMemo(() => {
    if (tableMode !== 'grouped' || groupedCaseStateFilter === 'all') return visibleRows;
    return (visibleRows as IncidentGroup[]).filter((row) => {
      const state = deriveReviewerCaseState(row);
      if (groupedCaseStateFilter === 'awaiting_review') return state.key === 'awaiting_review';
      return state.key !== 'awaiting_review';
    });
  }, [groupedCaseStateFilter, tableMode, visibleRows]);

  const groupedCaseQueueSummary = React.useMemo(() => {
    if (tableMode !== 'grouped') return null;
    const caseRows = (displayedRows as IncidentGroup[]) || [];
    const counts = {
      awaitingReview: 0,
      trackingFollowUp: 0,
      preparingEvidence: 0,
      closed: 0,
      staleAwaitingReview: 0,
    };
    for (const row of caseRows) {
      const state = deriveReviewerCaseState(row);
      if (state.key === 'awaiting_review') {
        counts.awaitingReview += 1;
        const openedDays = reviewerCaseOpenedDays(row);
        if (openedDays != null && openedDays >= 7) counts.staleAwaitingReview += 1;
      }
      else if (state.key === 'reviewer_engaged') counts.trackingFollowUp += 1;
      else if (state.key === 'preparing_evidence') counts.preparingEvidence += 1;
      else if (state.key === 'closed') counts.closed += 1;
    }
    return {
      total: caseRows.length,
      ...counts,
    };
  }, [displayedRows, tableMode]);

  // Tab Open/Closed is sent as status=; do not hide the raw API total for it.
  const rawObservationTotalCompatible = !(
    Boolean(String(filterWorkflowStatus || '').trim())
    || Boolean(String(filterMinAgeMinutes || '').trim())
    || filterHeatmapHour != null
    || Boolean(String(filterPolicyPack || '').trim())
    || Boolean(String(searchIncidentId || '').trim())
  );

  const reviewPopulationCompatible = React.useMemo(() => {
    const hasClientSideScopeDelta =
      Boolean(String(filterWorkflowStatus || '').trim())
      || Boolean(String(filterMinAgeMinutes || '').trim())
      || filterHeatmapHour != null
      || Boolean(String(filterPolicyPack || '').trim())
      || Boolean(String(searchIncidentId || '').trim())
      || Boolean(String(filterOpsQueue || '').trim())
      || Boolean(String(filterOpsAssignee || '').trim())
      || Boolean(String(filterIncidentStatus || '').trim())
      || tabValue !== 0;
    if (hasClientSideScopeDelta) return false;
    if (tableMode === 'grouped' && groupedCaseStateFilter !== 'all') return false;
    return true;
  }, [
    filterHeatmapHour,
    filterIncidentStatus,
    filterMinAgeMinutes,
    filterOpsAssignee,
    filterOpsQueue,
    filterPolicyPack,
    filterWorkflowStatus,
    groupedCaseStateFilter,
    searchIncidentId,
    tabValue,
    tableMode,
  ]);

  const groupedReviewDigestToken = React.useMemo(() => {
    if (tableMode !== 'grouped' || !groupedCaseQueueSummary?.awaitingReview) return '';
    const dayStamp = new Date().toISOString().slice(0, 10);
    return [
      scopedSiteId || '__site__',
      groupedCaseQueueSummary.awaitingReview,
      groupedCaseQueueSummary.staleAwaitingReview,
      dayStamp,
    ].join(':');
  }, [groupedCaseQueueSummary, scopedSiteId, tableMode]);

  const showGroupedReviewDigest = Boolean(
    groupedReviewDigestToken
    && groupedReviewDigestDismissedToken !== groupedReviewDigestToken,
  );

  const handleClearHeatmapHourFilter = useCallback(() => {
    setFilterHeatmapHour(null);
    setHeatmapPinnedIncidentId(null);
    setHeatmapViewFeedback(null);
    const next = new URLSearchParams(searchParams);
    next.delete('heatmap_hour');
    next.delete('exact_incident');
    setSearchParams(next, { replace: true });
  }, [searchParams, setFilterHeatmapHour, setSearchParams]);

  const handleClearPolicyPackFilter = useCallback(() => {
    setFilterPolicyPack('');
    const next = new URLSearchParams(searchParams);
    next.delete('policy_pack');
    setSearchParams(next, { replace: true });
  }, [searchParams, setFilterPolicyPack, setSearchParams]);

  const queueWorkflowHandoff = useMemo(() => {
    const workflow = String(searchParams.get('workflow') || '').trim().toLowerCase();
    const focus = String(searchParams.get('focus') || '').trim().toLowerCase();
    if (focus !== 'queue') return null;
    const reviewFocus = String(searchParams.get('review_focus') || '').trim().toLowerCase();
    const handoffReason = String(searchParams.get('handoff_reason') || '').trim().toLowerCase();

    if (workflow === 'live_rtsp_demo') {
      const demoScenario = getWebSummitDemoScenarioById(searchParams.get('scenarioId') || searchParams.get('scenario_id'));
      const cameraSource = String(searchParams.get('camera_source') || searchParams.get('cameraSource') || '').trim();
      return {
        eyebrow: 'Live RTSP Demo Review',
        title: 'Pedestrian / Forklift Interaction',
        description:
          'Risk: Pedestrian activity in forklift operating area. Use Review to inspect evidence, verify the incident, and log corrective follow-up for the live RTSP demo case.',
        contextChip: [demoScenario.zone || 'Forklift operating area', cameraSource === WEB_SUMMIT_DEMO_CAMERA_SOURCE ? 'Local RTSP demo feed' : '', demoScenario.windowLabel || 'Live demo session']
          .filter(Boolean)
          .join(' · '),
      };
    }

    if (workflow === 'safety_report') {
      const blockerLabel = reviewFocus === 'evidence_review'
        ? 'Evidence Review'
        : reviewFocus === 'action_owner'
          ? 'Action Owner'
          : reviewFocus === 'corrective_action'
            ? 'Corrective Action'
            : reviewFocus === 'impact_measurement'
              ? 'Impact Measurement'
              : 'Review';
      const zoneId = String(filterZoneId || searchParams.get('zone_id') || '').trim();
      const incidentType = String(filterIncidentType || searchParams.get('incident_type') || '').trim();
      const zoneLabel = zoneId ? (zoneNameById.get(zoneId) || zoneId) : '';
      const incidentLabel = incidentType ? incidentTypeLabel(incidentType) : '';
      const scopeLabel = [zoneLabel, incidentLabel].filter(Boolean).join(' · ');
      return {
        eyebrow: 'Safety Report Workflow Handoff',
        title: `${blockerLabel}: start from the scoped queue`,
        description: handoffReason === 'workflow_anchor_requires_reanchor'
          ? `This Safety Report has a stored workflow anchor, but it no longer points to one exact review record that can be reopened directly. Start from this scoped queue to complete ${blockerLabel.toLowerCase()} and re-anchor the report if needed.`
          : handoffReason === 'missing_workflow_anchor'
            ? `This Safety Report was created before workflow anchors were stored. Start from this scoped queue to complete ${blockerLabel.toLowerCase()} and unblock the legacy report.`
            : `Start from this scoped queue to complete ${blockerLabel.toLowerCase()} and unblock the report.`,
        contextChip: scopeLabel || undefined,
      };
    }

    if (workflow !== 'corrective_action') return null;

    const zoneId = String(filterZoneId || searchParams.get('zone_id') || '').trim();
    const incidentType = String(filterIncidentType || searchParams.get('incident_type') || '').trim();
    const deviceId = String(filterDeviceId || searchParams.get('device_id') || searchParams.get('device') || '').trim();
    const fromDate = String(searchParams.get('from_date') || '').trim();
    const toDate = String(searchParams.get('to_date') || '').trim();

    const zoneLabel = zoneId ? (zoneNameById.get(zoneId) || zoneId) : '';
    const incidentLabel = incidentType ? incidentTypeLabel(incidentType) : '';
    const deviceLabel = deviceId ? (deviceNameById.get(deviceId) || _warehouseUnitLabel(deviceId)) : '';
    const scopeParts = [zoneLabel, incidentLabel].filter(Boolean);
    const scopeLabel = scopeParts.join(' · ');
    const windowLabel = fromDate && toDate ? `${fromDate} to ${toDate}` : '';

    return {
      eyebrow: 'Corrective Action Workflow',
      title: 'Corrective action workflow: latest incidents for this area',
      description: scopeLabel
        ? `You are in the incident slice for ${scopeLabel}. Review the latest grouped alerts here before assigning or tracking follow-up work.`
        : 'You are in the scoped incident slice for corrective action. Review the latest grouped alerts here before assigning or tracking follow-up work.',
      contextChip: windowLabel || deviceLabel || scopeLabel || undefined,
    };
  }, [
    deviceNameById,
    filterDeviceId,
    filterIncidentType,
    filterZoneId,
    incidentTypeLabel,
    searchParams,
    zoneNameById,
  ]);

  const _incidentsNextStep = React.useMemo(() => {
    if (tableMode === 'grouped' && groupedCaseQueueSummary?.awaitingReview) {
      const awaiting = groupedCaseQueueSummary.awaitingReview;
      return {
        title: 'Review patterns needing review',
        detail: `${awaiting === 1 ? '1 pattern needs review' : `${awaiting} patterns need review`} before corrective action can be verified.`,
        label: 'Focus awaiting review',
        action: () => {
          setTabValue(1);
          setTableMode('grouped');
          setFilterOpsQueue('');
          setFilterIncidentStatus('');
          setFilterSeverity('');
        },
      };
    }

    const escalations = Number((opsQueues as any)?.queues?.escalations?.count || 0);
    const overdue = Number((opsQueues as any)?.queues?.overdue?.count || 0);
    const highRisk = Number((opsQueues as any)?.queues?.unassigned_high_risk?.count || 0);
    const openNow = Number(kpis?.open_now || 0);
    const topQueueIncident = _pickTopQueueIncident(opsQueues as any);

    if (escalations > 0) {
      return {
        title: 'Review escalations',
        detail: 'Escalations are waiting. Open the escalations queue and start with the first pattern.',
        label: 'Open escalations',
        action: () => {
          setTableMode('raw');
          setFilterOpsQueue('escalations');
          setPendingQueueAutoOpen(true);
        },
      };
    }
    if (overdue > 0) {
      return {
        title: 'Triage overdue incidents',
        detail: 'Overdue work is the highest remaining queue risk. Open the overdue queue first.',
        label: 'Open overdue queue',
        action: () => {
          setTableMode('raw');
          setFilterOpsQueue('overdue');
          setPendingQueueAutoOpen(true);
        },
      };
    }
    if (highRisk > 0) {
      return {
        title: 'Claim unassigned high-risk work',
        detail: 'High-risk incidents still need ownership. Open that queue before lower-priority review.',
        label: 'Open high-risk queue',
        action: () => {
          setTableMode('raw');
          setFilterOpsQueue('unassigned_high_risk');
          setPendingQueueAutoOpen(true);
        },
      };
    }
    if (topQueueIncident?.incident_id) {
      return {
        title: 'Go to the review queue',
        detail: 'Risk patterns are waiting for review. Start from the default queue.',
        label: 'Go to review queue',
        action: () =>
          navigate(
            buildScopedIncidentsPath(
              `/incidents?incident_id=${encodeURIComponent(topQueueIncident.incident_id)}&review=1`
            )
          ),
      };
    }
    if (openNow > 0) {
      return {
        title: 'Go to the review queue',
        detail: 'Risk patterns are waiting for review. Start from the default queue.',
        label: 'Go to review queue',
        action: () => {
          setTableMode('raw');
          setFilterOpsQueue('');
        },
      };
    }
    return {
      title: 'Queue is quiet',
      detail: 'No urgent queue slice needs action right now. Stay here for review or return to Overview for broader site state.',
      label: 'Return to Overview',
      action: () => navigate('/overview'),
    };
  }, [
    groupedCaseQueueSummary,
    kpis?.open_now,
    navigate,
    opsQueues,
    setFilterIncidentStatus,
    setFilterOpsQueue,
    setFilterSeverity,
    setPendingQueueAutoOpen,
    setTabValue,
    setTableMode,
    tableMode,
  ]);

  const handleSaveScope = useCallback(() => {
    const org = String(scopeOrgDraft || '').trim();
    const site = String(scopeSiteDraft || '').trim();
    try {
      if (org) localStorage.setItem('ui.scope.org_id', org);
      else localStorage.removeItem('ui.scope.org_id');
      if (site) localStorage.setItem('ui.scope.site_id', site);
      else localStorage.removeItem('ui.scope.site_id');
      if (org && site) localStorage.setItem('ui.scope.user_set', 'true');
      else localStorage.removeItem('ui.scope.user_set');
    } catch {
      // ignore
    }
    setScopeDialogOpen(false);
    setScopeNonce((x) => x + 1);
    try {
      void fetchData();
    } catch {
      // ignore
    }
  }, [scopeOrgDraft, scopeSiteDraft, fetchData]);

  const {
    openIncidentJson,
    acknowledgeIncidentDeepLink,
  } = useIncidentsPageOrchestration({
    pendingQueueAutoOpen,
    setPendingQueueAutoOpen,
    loading,
    tableMode,
    filterOpsQueue,
    visibleRows: visibleRows as any[],
    viewDialogOpen,
    selectedIncident,
    incidents,
    filteredIncidents,
    searchIncidentId,
    setSelectedGroupContext,
    setSelectedIncident,
    setViewDialogTab,
    setViewDialogOpen,
    fetchData,
    clearLoading,
    setIncidents,
    createDialogOpen,
    searchParams,
    setSearchParams,
    filterCameraId,
    setFilterCameraId,
    filterDeviceId,
    setFilterDeviceId,
    filterZoneId,
    setFilterZoneId,
    filterIncidentType,
    setFilterIncidentType,
    filterWorkflowStatus,
    setFilterWorkflowStatus,
    filterHeatmapHour,
    setFilterHeatmapHour,
    filterPolicyPack,
    setFilterPolicyPack,
    setFilterSeverity,
    setTableMode,
    setTabValue,
    setFilterOpsQueue,
    setSortModel,
    setSortBySeverity,
    customFrom,
    customTo,
    setTimePreset,
    setCustomFrom,
    setCustomTo,
    allCameras,
    isUuid: _isUuid,
    toLocalInputValue: _toLocalInputValue,
    setError,
    tabValue,
    filterSeverity,
    timeRange,
    setDrawerIncidentLoading,
    scopedSiteId,
    scopedOrgId: _scopedOrgId(),
  });
  const riskCategoryReportLink = React.useMemo(() => {
    if (!patternCaseReviewMode) return null;
    const source: any = selectedGroupContext || selectedIncident;
    if (!source) return null;
    const hazardClassKey = resolveIncidentHazardClassKey(source);
    if (!hazardClassKey) return null;
    return {
      hazardClassKey,
      path: buildHazardClassReportPath(hazardClassKey, {
        orgId: _scopedOrgId(),
        siteId: _scopedSiteId(),
        reportFrom: _toLocalInputValue(timeRange.fromDate || ''),
        reportTo: _toLocalInputValue(timeRange.toDate || ''),
        deviceId: String(source?.device_id || filterDeviceId || '').trim() || undefined,
        zoneId: String(source?.zone_id || filterZoneId || '').trim() || undefined,
      }),
    };
  }, [
    patternCaseReviewMode,
    selectedGroupContext,
    selectedIncident,
    timeRange.fromDate,
    timeRange.toDate,
    filterDeviceId,
    filterZoneId,
    scopedSiteId,
  ]);
  const occurrenceOpenedHandlerRef = React.useRef<(() => void) | null>(null);
  const handleOpenIncidentReviewPage = useCallback((
    incidentId?: string | null,
    opts?: { exact?: boolean; workflowId?: string | null; episodeId?: string | null },
  ) => {
    const id = String(incidentId || selectedIncident?.incident_id || '').trim();
    if (!id) return;
    const next = new URLSearchParams(searchParams);
    next.set('incident_id', id);
    next.set('review', '1');
    applyObservationEpisodeSearchParam(next, opts?.episodeId);
    const workflowId = String(
      opts?.workflowId
      || searchParams.get('workflow_id')
      || (selectedGroupContext as any)?.group_key
      || (selectedIncident as any)?.group_key
      || '',
    ).trim();
    if (workflowId) next.set('workflow_id', workflowId);
    if (opts?.exact) next.set('exact_incident', '1');
    else next.delete('exact_incident');
    navigate(buildScopedIncidentsPath(`/incidents?${next.toString()}`));
  }, [navigate, searchParams, selectedGroupContext, selectedIncident]);
  const handleExitIncidentReviewPage = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('review');
    next.delete('incident_id');
    applyObservationEpisodeSearchParam(next, null);
    next.delete('exact_incident');
    navigate(buildScopedIncidentsPath(`/incidents${next.toString() ? `?${next.toString()}` : ''}`));
    setViewDialogOpen(false);
  }, [navigate, searchParams, setViewDialogOpen]);
  const handleViewAffectedPatternOccurrences = useCallback((scope: PatternActionViewScope) => {
    setGroupPatternDialogOpen(false);
    setTableMode('raw');
    setFilterIncidentStatus('OPEN');
    setFilterOpsQueue('');
    setFilterWorkflowStatus('');
    if (scope.incidentType) setFilterIncidentType(scope.incidentType);
    if (scope.zoneId) setFilterZoneId(scope.zoneId);
    if (scope.cameraId) setFilterCameraId(scope.cameraId);
    if (scope.deviceId) setFilterDeviceId(scope.deviceId);
    // TODO: Deep-link related occurrences with group_key + status=OPEN + from/to so the affected set is inspectable.
  }, [
    setFilterCameraId,
    setFilterDeviceId,
    setFilterIncidentStatus,
    setFilterIncidentType,
    setFilterOpsQueue,
    setFilterWorkflowStatus,
    setFilterZoneId,
    setGroupPatternDialogOpen,
    setTableMode,
  ]);

  const handleOpenReviewSeriesQueue = useCallback(() => {
    const source: any = selectedGroupContext || selectedIncident;
    if (!source) {
      handleExitIncidentReviewPage();
      return;
    }
    setTableMode('raw');
    if (source?.incident_type) setFilterIncidentType(String(source.incident_type));
    if (source?.zone_id) setFilterZoneId(String(source.zone_id));
    if (source?.camera_id) setFilterCameraId(String(source.camera_id));
    if (source?.device_id) setFilterDeviceId(String(source.device_id));
    handleExitIncidentReviewPage();
  }, [
    handleExitIncidentReviewPage,
    selectedGroupContext,
    selectedIncident,
    setFilterCameraId,
    setFilterDeviceId,
    setFilterIncidentType,
    setFilterZoneId,
    setTableMode,
  ]);
  const openIncidentReviewSurface = useCallback((row: any) => {
    handleOpenIncidentReviewPage(observationReviewIncidentId(row), observationReviewOpenOptions(row, tableMode));
  }, [handleOpenIncidentReviewPage, tableMode]);

  const [heatmapOccurrenceWindow, setHeatmapOccurrenceWindow] = React.useState<{
    fromDate?: string;
    toDate?: string;
  } | null>(null);

  const {
    handleGroupPatternAction,
    handleGroupedHeatmapCellSelect,
    handleGroupPatternActionCompleted,
    canManageGroupPatterns,
    heatmapOpening,
  } = useGroupedPatternReviewActions({
    incidents,
    selectedIncident,
    setSelectedIncident,
    selectedGroupContext,
    timeRange,
    heatmapWindow: heatmapOccurrenceWindow,
    searchParams,
    setSearchParams,
    setError,
    setHeatmapViewFeedback,
    onHeatmapOccurrencePinned: setHeatmapPinnedIncidentId,
    acknowledgeIncidentDeepLink,
    setFilterHeatmapHour,
    handleOpenIncidentReviewPage,
    fetchData,
    setGroupPatternDialogGroup,
    setGroupPatternDialogMode,
    setGroupPatternDialogOpen,
    setGroupPatternActionBusy,
    canManageIncidentWorkflow,
    tableMode,
    reviewMode,
    onOccurrenceOpened: () => {
      occurrenceOpenedHandlerRef.current?.();
    },
  });

  const { columns, groupColumns } = useIncidentsTableColumns({
    zoneNameById,
    cameraNameById,
    deviceNameById,
    locationLookups,
    incidentTypeLabel,
    incidentRowTypeLabel,
    incidentPolicyLabel: _incidentPolicyLabel,
    findingForIncident: _findingForIncident,
    severityRank: _severityRank,
    severityChipSx: _severityChipSx,
    severityIcon: _severityIcon,
    openEvidencePopover,
    openIncidentDetails: openIncidentReviewSurface,
    openIncidentJson,
    renderWorkOrderCell: _renderWorkOrderCell,
    handleEnsureOdooTicket,
    handleSyncFromOdoo,
    getOdooStateColor,
    deriveLifecycle,
    getOperationalStatusColor: (status) => getOperationalStatusColor(status as any),
    formatDuration: _formatDuration,
    canManageGroupPatterns,
    groupPatternActionBusy,
    onGroupPatternAction: handleGroupPatternAction,
    operatorMode: incidentsUiMode !== 'advanced',
  });

  const scrollToSection = useCallback((target: React.RefObject<HTMLDivElement | null>) => {
    target.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    if ((searchParams.get('focus') || '').trim().toLowerCase() !== 'queue') return;
    const handle = window.requestAnimationFrame(() => {
      scrollToSection(queueBlockRef);
    });
    return () => window.cancelAnimationFrame(handle);
  }, [scrollToSection, searchParams]);

  const resetOperatorSurface = useCallback(() => {
    setOperatorPreset('');
    setFilterIncidentType('');
    setFilterZoneId('');
    setFilterCameraId('');
    setFilterDeviceId('');
    setFilterWorkflowStatus('');
    setFilterOpsAssignee('');
    setFilterOpsQueue('');
    setSearchIncidentId('');
    setFilterPolicyPack('');
    setGroupedCaseStateFilter('all');
    setTableFiltersOpen(false);
  }, [
    setFilterCameraId,
    setFilterDeviceId,
    setFilterIncidentType,
    setFilterOpsAssignee,
    setFilterOpsQueue,
    setFilterPolicyPack,
    setFilterWorkflowStatus,
    setFilterZoneId,
    setGroupedCaseStateFilter,
    setOperatorPreset,
    setSearchIncidentId,
  ]);

  const handleOpenCurrentQueue = useCallback(() => {
    resetOperatorSurface();
    setTabValue(1);
    setTableMode(tableMode);
    setFilterIncidentStatus('');
    setFilterSeverity('');
    scrollToSection(queueBlockRef);
  }, [resetOperatorSurface, scrollToSection, setFilterIncidentStatus, setFilterSeverity, setTabValue, setTableMode, tableMode]);

  const handleHotspotSelect = useCallback((target: { type: 'zone' | 'camera'; id: string }) => {
    resetOperatorSurface();
    setTabValue(1);
    setTableMode('grouped');
    setFilterIncidentStatus('');
    setFilterSeverity('');
    if (target.type === 'zone') {
      setFilterZoneId(target.id);
      setFilterCameraId('');
    } else {
      setFilterCameraId(target.id);
      setFilterZoneId('');
    }
    scrollToSection(queueBlockRef);
  }, [
    resetOperatorSurface,
    scrollToSection,
    setFilterCameraId,
    setFilterIncidentStatus,
    setFilterSeverity,
    setFilterZoneId,
    setTabValue,
    setTableMode,
  ]);

  const handleOpenIncidentsMetric = useCallback(() => {
    handleOpenCurrentQueue();
  }, [handleOpenCurrentQueue]);

  const handleHighRiskMetric = useCallback(() => {
    resetOperatorSurface();
    setTabValue(1);
    setTableMode(tableMode);
    setFilterIncidentStatus('');
    setFilterSeverity('high');
    scrollToSection(queueBlockRef);
  }, [resetOperatorSurface, scrollToSection, setFilterIncidentStatus, setFilterSeverity, setTabValue, setTableMode, tableMode]);

  const handleRecurringMetric = useCallback(() => {
    resetOperatorSurface();
    setTabValue(1);
    if (tableMode === 'raw') {
      setTableMode('raw');
      setFilterOpsQueue('overdue');
      setFilterIncidentStatus('');
      setFilterSeverity('');
    } else {
      setTableMode('grouped');
      setFilterIncidentStatus('');
      setFilterSeverity('');
    }
    scrollToSection(queueBlockRef);
  }, [resetOperatorSurface, scrollToSection, setFilterIncidentStatus, setFilterOpsQueue, setFilterSeverity, setTabValue, setTableMode, tableMode]);

  const handleReviewThroughputMetric = useCallback(() => {
    resetOperatorSurface();
    setTabValue(0);
    setTableMode('raw');
    setFilterIncidentStatus('');
    setFilterWorkflowStatus('Reviewed');
    setFilterSeverity('');
    setTableFiltersOpen(true);
    scrollToSection(queueBlockRef);
  }, [resetOperatorSurface, scrollToSection, setFilterIncidentStatus, setFilterSeverity, setFilterWorkflowStatus, setTabValue, setTableFiltersOpen, setTableMode]);

  const handleOpenReviewerHealthMetric = useCallback(() => {
    resetOperatorSurface();
    setTabValue(1);
    setTableMode('grouped');
    setGroupedCaseStateFilter('awaiting_review');
    setFilterIncidentStatus('');
    setFilterSeverity('');
    scrollToSection(queueBlockRef);
  }, [resetOperatorSurface, scrollToSection, setFilterIncidentStatus, setFilterSeverity, setTabValue, setTableMode]);

  const fullHistoryMode = useMemo(
    () => isFullCaseHistoryReviewScope(searchParams),
    [searchParams],
  );

  const caseHistoryBounds = useMemo(() => {
    if (!fullHistoryMode) return null;
    const fromUrl = String(searchParams.get('from_date') || '').trim();
    const toUrl = String(searchParams.get('to_date') || '').trim();
    if (fromUrl || toUrl) {
      return { fromIso: fromUrl || null, toIso: toUrl || null };
    }
    return resolveFullCaseHistoryTimeBounds(selectedGroupContext as IncidentGroup | null);
  }, [fullHistoryMode, searchParams, selectedGroupContext]);

  const incidentIdFromUrl = String(searchParams.get('incident_id') || '').trim() || null;
  const caseIdFromUrl = String(
    searchParams.get('case_id') || searchParams.get('safety_case_id') || '',
  ).trim() || null;
  const inferredWorkflowId = String(
    workflowIdFromUrl
    || (selectedGroupContext as any)?.group_key
    || (selectedIncident as any)?.group_key
    || '',
  ).trim() || null;
  const activeReviewWindow = useMemo(
    () => resolveActiveReviewWindowIso(searchParams, timeRange, {
      timePreset,
      customFrom,
      customTo,
    }) || {},
    [customFrom, customTo, searchParams, timePreset, timeRange],
  );

  const {
    context: riskPatternWorkflowReviewContext,
    dashboardRow: riskPatternWorkflowDashboardRow,
    loading: riskPatternWorkflowReviewLoading,
    error: riskPatternWorkflowReviewError,
    applyCreatedSafetyCase,
  } = useRiskPatternWorkflowReviewContext({
    workflowId: inferredWorkflowId,
    incidentId: incidentIdFromUrl,
    caseId: caseIdFromUrl,
    deviceId: String((selectedGroupContext as any)?.device_id || filterDeviceId || '').trim() || null,
    zoneId: String((selectedGroupContext as any)?.zone_id || filterZoneId || '').trim() || null,
    cameraId: String((selectedGroupContext as any)?.camera_id || filterCameraId || '').trim() || null,
    riskType: String((selectedGroupContext as any)?.incident_type || filterIncidentType || '').trim() || null,
    orgId: _scopedOrgId(),
    siteId: scopedSiteId,
    reportFrom: activeReviewWindow.fromIso,
    reportTo: activeReviewWindow.toIso,
    reviewScope: searchParams.get('review_scope'),
  });

  const afterSignedScanReview = useMemo(
    () => resolveEffectiveAfterSignedScanReviewContext(searchParams, riskPatternWorkflowReviewContext),
    [searchParams, riskPatternWorkflowReviewContext],
  );

  const reviewWindowContext = useMemo(
    () => resolveReviewWindowContext({
      timeRange,
      afterSignedScanReview,
      fullHistoryMode,
      caseHistoryBounds,
    }),
    [afterSignedScanReview, caseHistoryBounds, fullHistoryMode, timeRange],
  );

  const afterSignedScanWindowBounds = useMemo(
    () => resolveAfterSignedScanWindowBounds(afterSignedScanReview, timeRange.toDate),
    [afterSignedScanReview, timeRange.toDate],
  );

  const afterSignedScanScopedIncidents = useMemo(() => {
    if (!afterSignedScanWindowBounds) return [];
    return filterIncidentsInAfterSignedScanWindow(
      incidents,
      afterSignedScanWindowBounds.startIso,
      afterSignedScanWindowBounds.endIso,
      {
      incidentType: filterIncidentType || searchParams.get('policy_type') || selectedIncident?.incident_type,
      zoneId: filterZoneId || searchParams.get('zone_id') || selectedIncident?.zone_id,
      cameraId: filterCameraId || searchParams.get('camera_id') || selectedIncident?.camera_id,
      deviceId: filterDeviceId || searchParams.get('device_id') || selectedIncident?.device_id,
    });
  }, [
    afterSignedScanWindowBounds,
    filterCameraId,
    filterDeviceId,
    filterIncidentType,
    filterZoneId,
    incidents,
    searchParams,
    selectedIncident,
  ]);

  const afterSignedScanLatestObservation = useMemo(() => {
    if (!afterSignedScanReview.active) return null;
    if (afterSignedScanScopedIncidents.length > 0) {
      const latest = afterSignedScanScopedIncidents.reduce((current, candidate) => (
        incidentObservationTimestamp(candidate) > incidentObservationTimestamp(current) ? candidate : current
      ));
      return latest.last_edge_update_at || latest.updated_at || latest.created_at || null;
    }
    return afterSignedScanReview.newActivityLatestObservation;
  }, [
    afterSignedScanReview.active,
    afterSignedScanReview.newActivityLatestObservation,
    afterSignedScanScopedIncidents,
  ]);

  const handleReviewFullSafetyCaseHistory = useCallback(() => {
    const bounds = resolveFullCaseHistoryTimeBounds(selectedGroupContext as IncidentGroup | null);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      activateFullCaseHistoryReviewParams(next, {
        fromDate: bounds.fromIso,
        toDate: bounds.toIso,
      });
      return next;
    }, { replace: true });
    if (bounds.fromIso && bounds.toIso) {
      setTimePreset('custom');
      setCustomFrom(_toLocalInputValue(bounds.fromIso));
      setCustomTo(_toLocalInputValue(bounds.toIso));
    }
  }, [selectedGroupContext, setCustomFrom, setCustomTo, setSearchParams, setTimePreset]);

  const handleRiskPatternReviewEvidence = useCallback(() => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('review_focus', 'evidence_review');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const riskPatternWorkflowReviewActions = useMemo(() => {
    if (!riskPatternWorkflowReviewContext) return undefined;
    return buildRiskPatternWorkflowReviewActions({
      context: riskPatternWorkflowReviewContext,
      dashboardRow: riskPatternWorkflowDashboardRow,
      navigate,
      orgId: _scopedOrgId(),
      siteId: scopedSiteId,
      reportFrom: activeReviewWindow.fromIso,
      reportTo: activeReviewWindow.toIso,
      scanScopeStart: afterSignedScanReview.active
        ? (afterSignedScanReview.scanScopeStart || activeReviewWindow.fromIso)
        : undefined,
      scanScopeEnd: afterSignedScanReview.active
        ? (afterSignedScanReview.scanScopeEnd || activeReviewWindow.toIso)
        : undefined,
      signedScanAt: afterSignedScanReview.active ? afterSignedScanReview.signedScanAt : undefined,
      signedScanScopeStart: afterSignedScanReview.active
        ? afterSignedScanReview.signedScanScopeStart
        : undefined,
      signedScanScopeEnd: afterSignedScanReview.active
        ? afterSignedScanReview.signedScanScopeEnd
        : undefined,
      latestSignedScanId: afterSignedScanReview.active
        ? afterSignedScanReview.latestSignedScanId
        : undefined,
      newActivityWindowStart: afterSignedScanReview.active
        ? (afterSignedScanReview.newActivityWindowStart || afterSignedScanReview.signedScanAt)
        : undefined,
      onReviewEvidence: handleRiskPatternReviewEvidence,
      onMarkFalsePositive: handleMarkFalsePositive,
      onNeedsMoreReview: handleMarkInReview,
      onContinueReview: handleRiskPatternReviewEvidence,
      canConfigureScheduledScans: hasAccessCapability(capabilityProfile.capabilities, 'scan:configure'),
      onSafetyCaseCreated: applyCreatedSafetyCase,
    });
  }, [
    afterSignedScanReview.active,
    afterSignedScanReview.latestSignedScanId,
    afterSignedScanReview.newActivityWindowStart,
    afterSignedScanReview.scanScopeEnd,
    afterSignedScanReview.scanScopeStart,
    afterSignedScanReview.signedScanAt,
    afterSignedScanReview.signedScanScopeEnd,
    afterSignedScanReview.signedScanScopeStart,
    applyCreatedSafetyCase,
    capabilityProfile.capabilities,
    handleMarkFalsePositive,
    handleMarkInReview,
    handleRiskPatternReviewEvidence,
    navigate,
    riskPatternWorkflowDashboardRow,
    riskPatternWorkflowReviewContext,
    activeReviewWindow.fromIso,
    activeReviewWindow.toIso,
    scopedSiteId,
  ]);

  const afterSignedScanTimelineAnchors = useMemo(() => {
    const signedScanAt = String(afterSignedScanReview.signedScanAt || '').trim();
    if (!signedScanAt) return null;
    const fullHistoryBounds = resolveFullCaseHistoryTimeBounds(selectedGroupContext as IncidentGroup | null);
    return {
      signedScanAt,
      reviewEnd: timeRange.toDate || null,
      latestSignedScanId: afterSignedScanReview.latestSignedScanId
        || riskPatternWorkflowReviewContext?.latestSignedScanReportId
        || null,
      signedScanScopeStart: afterSignedScanReview.signedScanScopeStart
        || riskPatternWorkflowReviewContext?.signedScanScopeStart
        || null,
      signedScanScopeEnd: afterSignedScanReview.signedScanScopeEnd
        || riskPatternWorkflowReviewContext?.signedScanScopeEnd
        || null,
      fullHistoryBounds: {
        fromDate: fullHistoryBounds.fromIso,
        toDate: fullHistoryBounds.toIso,
      },
    };
  }, [
    afterSignedScanReview.latestSignedScanId,
    afterSignedScanReview.signedScanAt,
    afterSignedScanReview.signedScanScopeEnd,
    afterSignedScanReview.signedScanScopeStart,
    riskPatternWorkflowReviewContext?.latestSignedScanReportId,
    riskPatternWorkflowReviewContext?.signedScanScopeEnd,
    riskPatternWorkflowReviewContext?.signedScanScopeStart,
    selectedGroupContext,
    timeRange.toDate,
  ]);

  const syncReviewWindowPresetFromBounds = useCallback((fromIso?: string | null, toIso?: string | null) => {
    if (!fromIso && !toIso) return;
    setTimePreset('custom');
    if (fromIso) setCustomFrom(_toLocalInputValue(fromIso));
    if (toIso) setCustomTo(_toLocalInputValue(toIso));
  }, [setCustomFrom, setCustomTo, setTimePreset]);

  const handleAfterSignedScanTimelineChange = useCallback((change: AfterSignedScanTimelineChange) => {
    if (!afterSignedScanTimelineAnchors) return;
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      applyAfterSignedScanTimelineChange(next, change, afterSignedScanTimelineAnchors);
      return next;
    }, { replace: true });

    if (change.option === 'full_case_history') {
      syncReviewWindowPresetFromBounds(
        afterSignedScanTimelineAnchors.fullHistoryBounds?.fromDate,
        afterSignedScanTimelineAnchors.fullHistoryBounds?.toDate,
      );
      return;
    }

    const startIso = change.option === 'since_latest_scan'
      ? afterSignedScanTimelineAnchors.signedScanAt
      : (change.scanScopeStart || afterSignedScanTimelineAnchors.signedScanAt);
    const endIso = change.scanScopeEnd || afterSignedScanTimelineAnchors.reviewEnd;
    syncReviewWindowPresetFromBounds(startIso, endIso);
  }, [afterSignedScanTimelineAnchors, setSearchParams, syncReviewWindowPresetFromBounds]);

  const afterSignedScanTimelineProps = useMemo(() => {
    if (!afterSignedScanTimelineAnchors) return null;
    const isStateC = riskPatternWorkflowReviewContext
      ? resolveRiskPatternReviewPageState(riskPatternWorkflowReviewContext) === 'state_c'
      : false;
    if (!afterSignedScanReview.active && !isStateC) return null;
    return {
      signedScanAt: afterSignedScanTimelineAnchors.signedScanAt,
      timelineMode: afterSignedScanReview.timelineMode,
      windowStartIso: afterSignedScanReview.scanScopeStart
        || afterSignedScanReview.newActivityWindowStart
        || afterSignedScanTimelineAnchors.signedScanAt,
      windowEndIso: afterSignedScanReview.scanScopeEnd || activeReviewWindow.toIso,
    };
  }, [
    activeReviewWindow.toIso,
    afterSignedScanReview.active,
    afterSignedScanReview.newActivityWindowStart,
    afterSignedScanReview.scanScopeEnd,
    afterSignedScanReview.scanScopeStart,
    afterSignedScanReview.timelineMode,
    afterSignedScanTimelineAnchors,
    riskPatternWorkflowReviewContext,
  ]);

  const handleResolveAfterSignedScanStartBeforeSignedScan = useCallback((
    action: 'full_history' | 'reset_start',
  ) => {
    if (action === 'full_history') {
      handleReviewFullSafetyCaseHistory();
      return;
    }
    handleAfterSignedScanTimelineChange({ option: 'since_latest_scan' });
  }, [handleAfterSignedScanTimelineChange, handleReviewFullSafetyCaseHistory]);

  const handleOpenProofPackage = useIncidentsProofPackageNavigation({
    canOpenArtifactEntry,
    navigate,
    searchParams,
    selectedGroupContext,
    selectedIncident,
    filterDeviceId,
    filterZoneId,
    filterCameraId,
    filterIncidentType,
    timeRange,
    riskPatternWorkflowDashboardRow,
    riskPatternWorkflowReviewContext,
    getScopedOrgId: _scopedOrgId,
    getScopedSiteId: _scopedSiteId,
  });

  const reviewPage = useIncidentsReviewPage({
    reviewMode,
    reviewExactMode,
    heatmapOpening,
    searchParams,
    heatmapPinnedIncidentId,
    setHeatmapPinnedIncidentId,
    selectedIncident,
    selectedGroupContext,
    setSelectedIncident,
    incidents,
    handleOpenIncidentReviewPage,
  });

  const incidentDrawerCtx = {
    viewDialogOpen: reviewMode ? true : viewDialogOpen, setViewDialogOpen: reviewMode ? handleExitIncidentReviewPage : setViewDialogOpen,
    selectedIncident, selectedGroupContext,
    renderMode: reviewMode ? 'page' : 'drawer',
    reviewFocus: String(searchParams.get('review_focus') || '').trim().toLowerCase(),
    onOpenReviewPage: (incidentId?: string | null) => handleOpenIncidentReviewPage(incidentId, { exact: true }),
    onOpenReviewPageFullProof: (incidentId?: string | null) =>
      openFullProofReviewHop({
        incidentId,
        open: handleOpenIncidentReviewPage,
        currentlyExact: reviewExactMode,
        workflowId: workflowIdFromUrl,
      }),
    viewDialogTab, setViewDialogTab,
    drawerAdvancedOpen, setDrawerAdvancedOpen,
    drawerIncidentLoading, drawerBusy,
    incidentActivityItems, incidentActivityLoading, incidentActivityError,
    incidentComments, incidentCommentsLoading, incidentCommentsError,
    incidentSafetyEpisodes, incidentSafetyEpisodesLoading, incidentSafetyEpisodesError,
    incidentSafetyEpisodesAvailable,
    latestSnapshotObjectUrl, latestSnapshotLoading, latestSnapshotError,
    latestVideoObjectUrl, latestVideoLoading, latestVideoError,
    latestVideoIdentity, loadEvidenceMoment,
    opsStateDraft, setOpsStateDraft,
    opsAssigneeDraft, setOpsAssigneeDraft,
    opsTeamDraft, setOpsTeamDraft,
    opsSlaTargetDraft, setOpsSlaTargetDraft,
    measurementBaselineDraft, setMeasurementBaselineDraft,
    measurementFollowUpDraft, setMeasurementFollowUpDraft,
    measurementMeasuredAtDraft, setMeasurementMeasuredAtDraft,
    measurementOutcomeStatusDraft, setMeasurementOutcomeStatusDraft,
    measurementNoteDraft, setMeasurementNoteDraft,
    measurementImpactWaivedDraft, setMeasurementImpactWaivedDraft,
    correctiveActionTypeDraft, setCorrectiveActionTypeDraft,
    correctiveActionPriorityDraft, setCorrectiveActionPriorityDraft,
    correctiveActionDueDateDraft, setCorrectiveActionDueDateDraft,
    correctiveActionVerificationMethodDraft, setCorrectiveActionVerificationMethodDraft,
    workflowId,
    noteDraft, setNoteDraft,
    severityDraft, setSeverityDraft,
    suppressionsLoading, suppressionsError,
    edgeSuppressions, matchingSuppressions,
    ppeMissingZoneDiag, ppeMissingZoneDiagLoading, ppeMissingZoneDiagError,
    runbookTemplates, incidentRunbookBusy, setIncidentRunbookBusy,
    _refreshSelectedIncident: refreshSelectedIncident,
    handleAcknowledge, handleMarkInReview,
    handleAddCommentShortcut, handleSaveNote, handleSaveOps, handleSaveFollowupOwner,
    handleSaveCoverageReviewAttestation, handleSaveZoneControlsConfirmation,
    handleSaveMeasurement, handleSaveCorrectiveAction,
    workflowCorrectiveActions, workflowImpactSummary, workflowClaimReadiness, refreshWorkflowCorrectiveSummary,
    claimReadinessRefreshKey,
    handleAddWorkflowEvidenceNote, handleUpdateWorkflowActionStatus,
    handleOverrideSeverity,
    handleMarkFalsePositive, openResolveDialogWithOutcome, handleExportEvidence,
    handleOpenThresholdTuning, handleOpenPolicyEditor,
    handleClearSuppressionForIncident,
    handleEnsureWorkOrder, handleSyncToOdoo, handleSyncFromOdoo,
    _loadEdgeSuppressions: loadEdgeSuppressions,
    _loadPpeMissingZoneDiag: loadPpeMissingZoneDiag,
    fetchEvidenceFileBlob, fetchLatestSnapshotBlob, fetchLatestVideoBlob, downloadBlob,
    _bestSnapshotAttachment, _bestVideoAttachment, _overlaySnapshotBlob,
    _humanLabelCompact, _incidentTypeLabel: incidentTypeLabel, _findingForIncident,
    _severityChipSx, _severityIcon, _opsStateLabel,
    _ppeMissingItemsFromIncident, _ppeSnapshotItemsFromIncident,
    _ppeStabilizedStateFromIncident, _ppeItemsSummary,
    getOdooStateColor,
    derivePolicyNotificationRoutes, buildContextBundle,
    canManageIncidentWorkflow, canCommentOnIncident,
    canOverrideIncidentSeverity, canExportIncidentEvidence,
    canAdjustIncidentThreshold, canOpenAuditTrail, canOpenCopilot,
    canRunIncidentRunbooks, canManageIncidentSuppressions, canUseOpsAutomation,
    isAdvancedIncidentsUi: isAdvancedIncidentsUi,
    navigate, activeScope, commentInputRef,
    _openReportDialog: handleOpenProofPackage,
    setSuppressDialogOpen, setCloseDialogOpen, setCloseReason,
    IncidentEvidenceAttachmentPreview, PolicyNotificationStatusChip,
    deriveLifecycle, edgeSuppressionsByDevice,
    executeRunbookServer,
    scopedSiteId, user, opsStateResult,
    allPolicies,
    allZones,
    zoneNameById, cameraNameById,
    incidentRowTypeLabel,
    setTableMode, setFilterOpsQueue,
    setFilterIncidentType, setFilterSeverity, setFilterCameraId, setFilterZoneId, setFilterDeviceId,
    fetchData, _titleCase,
    onGroupPatternAction: handleGroupPatternAction,
    canManageGroupPatterns,
    groupPatternActionBusy,
    onGroupedHeatmapCellSelect: handleGroupedHeatmapCellSelect,
    groupedHeatmapCellSelectCaption:
      'Open the newest matching occurrence for that UTC hour. Representative event review expands below.',
    reviewTimeRange: {
      fromDate: timeRange.fromDate || undefined,
      toDate: timeRange.toDate || undefined,
      label: timeRange.label,
    },
    reviewWindowContext,
    reviewExactMode,
    reviewSurfaceMode,
    reviewHeatmapHourActive: Boolean(String(searchParams.get('heatmap_hour') || '').trim()),
    workflowFeedback,
    clearWorkflowFeedback,
    riskCategoryReportLink,
    heatmapOpening,
    heatmapPageFeedback: heatmapViewFeedback || error,
    heatmapViewingIncidentId: heatmapPinnedIncidentId,
    onHeatmapWindowChange: setHeatmapOccurrenceWindow,
    onViewPatternOccurrences: patternCaseReviewMode ? handleOpenReviewSeriesQueue : undefined,
    registerOccurrenceOpenedHandler: (handler: (() => void) | null) => {
      occurrenceOpenedHandlerRef.current = handler;
    },
    afterSignedScanReview,
    afterSignedScanScopedActivityCount: afterSignedScanReview.active
      ? (afterSignedScanScopedIncidents.length || afterSignedScanReview.newActivityCount)
      : null,
    afterSignedScanLatestObservation,
    onReviewFullSafetyCaseHistory: handleReviewFullSafetyCaseHistory,
    onAfterSignedScanTimelineChange: afterSignedScanTimelineProps
      ? handleAfterSignedScanTimelineChange
      : undefined,
    onResolveAfterSignedScanStartBeforeSignedScan: afterSignedScanTimelineProps
      ? handleResolveAfterSignedScanStartBeforeSignedScan
      : undefined,
    afterSignedScanTimeline: afterSignedScanTimelineProps
      ? {
          ...afterSignedScanTimelineProps,
          onTimelineChange: handleAfterSignedScanTimelineChange,
          onResolveStartBeforeSignedScan: handleResolveAfterSignedScanStartBeforeSignedScan,
        }
      : null,
    riskPatternWorkflowReviewContext,
    riskPatternWorkflowReviewLoading,
    riskPatternWorkflowReviewError,
    riskPatternWorkflowReviewActions,
    observationParentCaseCount: reviewPage.observationParentCaseCount,
    observationReviewQueueIncidents: reviewPage.reviewSeriesIncidents,
    patternScopeIncidents: reviewPage.patternScopeIncidents,
  };
  const groupPatternZoneLabel = React.useMemo(() => {
    const zoneId = String(groupPatternDialogGroup?.zone_id || '').trim();
    if (!zoneId) return '';
    return zoneNameById.get(zoneId) || zoneId;
  }, [groupPatternDialogGroup?.zone_id, zoneNameById]);

  const patchLocalPatternOwner = useCallback((
    owner: string | null | undefined,
    team?: string | null,
  ) => {
    const nextOwner = String(owner || '').trim();
    if (!nextOwner) return;
    setSelectedIncident((prev) => (
      applyLocalPatternOwnerAssignment(prev as any, nextOwner, team) as Incident | null
    ));
    setSelectedGroupContext((prev) => (
      applyLocalPatternOwnerAssignment(prev as any, nextOwner, team) as IncidentGroup | null
    ));
  }, [setSelectedGroupContext, setSelectedIncident]);

  const handlePatternGroupActionInBackground = useCallback(
    (payload: {
      groupKey: string;
      body: IncidentGroupActionRequest;
      mode: IncidentGroupPatternActionMode;
      matchedCount: number;
      patternLabel: string;
    }) => {
      setGroupPatternDialogOpen(false);
      if (payload.mode === 'assign') {
        // Optimistic: open case UI was still showing Assign after server success because
        // fetchData refreshes the queue but not selectedIncident / selectedGroupContext.
        patchLocalPatternOwner(payload.body.pattern_owner, payload.body.pattern_owner_team);
      }
      setPatternActionSnack({
        open: true,
        message: patternGroupActionStartedMessage(
          payload.mode,
          payload.matchedCount,
          payload.patternLabel,
        ),
        severity: 'info',
      });
      void runPatternGroupActionInBackground(payload, {
        onSuccess: async (result) => {
          if (payload.mode === 'assign') {
            patchLocalPatternOwner(
              result.canonical_pattern_owner ?? payload.body.pattern_owner,
              result.canonical_pattern_owner_team ?? payload.body.pattern_owner_team,
            );
          }
          setGroupPatternActionBusy(true);
          try {
            await fetchData();
          } finally {
            setGroupPatternActionBusy(false);
          }
          setPatternActionSnack({
            open: true,
            message: patternGroupActionSuccessMessage(
              payload.mode,
              result,
            ),
            severity: 'success',
          });
        },
        onError: (message) => {
          setPatternActionSnack({
            open: true,
            message,
            severity: 'error',
          });
        },
      });
    },
    [fetchData, patchLocalPatternOwner, setGroupPatternActionBusy],
  );

  const groupPatternCameraLabel = React.useMemo(() => {
    const cameraId = String(groupPatternDialogGroup?.camera_id || '').trim();
    if (!cameraId) return '';
    return cameraNameById.get(cameraId) || cameraId;
  }, [cameraNameById, groupPatternDialogGroup?.camera_id]);

  const groupedReviewModeActive = patternCaseReviewMode;
  const resolveIncidentId = groupedReviewTargetIncidentId;
  const groupedEvidenceResolvePresentation = closeReviewOutcomePreset
    ? (
      observationDispositionMode
        ? observationDispositionResolvePresentation(closeReviewOutcomePreset)
        : groupedReviewModeActive
          ? groupedEvidenceReviewResolvePresentation(closeReviewOutcomePreset)
          : null
    )
    : null;

  const incidentDialogs = (
    <IncidentsDialogStack
      thresholdDialogOpen={thresholdDialogOpen}
      closeDialogOpen={closeDialogOpen}
      closeReviewOutcomePreset={closeReviewOutcomePreset}
      resolveIncidentId={resolveIncidentId}
      groupedEvidenceResolvePresentation={groupedEvidenceResolvePresentation}
      groupPatternDialogOpen={groupPatternDialogOpen}
      selectedIncident={selectedIncident}
      observationDispositionMode={observationDispositionMode}
      closeReason={closeReason}
      drawerBusy={drawerBusy}
      canManageIncidentWorkflow={canManageIncidentWorkflow}
      canAdjustIncidentThreshold={canAdjustIncidentThreshold}
      scopedSiteId={scopedSiteId}
      scopedOrgId={_scopedOrgId()}
      groupPatternDialogMode={groupPatternDialogMode}
      groupPatternDialogGroup={groupPatternDialogGroup}
      timeRange={timeRange}
      canManageGroupPatterns={canManageGroupPatterns}
      onCloseThreshold={() => setThresholdDialogOpen(false)}
      onCloseResolve={() => {
        setCloseDialogOpen(false);
        setCloseReviewOutcomePreset(null);
      }}
      onCloseGroupPattern={() => setGroupPatternDialogOpen(false)}
      onApplyThreshold={(adjustment, autoDeploy) => handleApplyThresholdAdjustmentWith(adjustment, autoDeploy)}
      onOpenPolicyEditor={handleOpenPolicyEditor}
      onResolve={({ reason, reviewOutcome, note, tags }) => handleCloseIncidentWith(reason, note, tags, reviewOutcome)}
      onGroupPatternCompleted={() => void handleGroupPatternActionCompleted()}
      patternVolumeCount={Math.max(
        0,
        getIncidentPatternReadVolumeCount(selectedGroupContext as any),
        getIncidentPatternReadVolumeCount(selectedIncident as any),
      )}
      zoneDisplayLabel={groupPatternZoneLabel}
      cameraDisplayLabel={groupPatternCameraLabel}
      onViewAffectedOccurrences={handleViewAffectedPatternOccurrences}
      onRunPatternActionInBackground={handlePatternGroupActionInBackground}
    />
  );

  if (reviewMode) {
    return (
      <IncidentsReviewPage
        {...reviewPage}
        reviewSurfaceMode={reviewSurfaceMode}
        reviewWindowContext={reviewWindowContext}
        selectedIncident={selectedIncident}
        selectedGroupContext={selectedGroupContext}
        error={error}
        onClearError={() => setError(null)}
        canOpenArtifactEntry={canOpenArtifactEntry}
        onExitReview={handleExitIncidentReviewPage}
        onOpenProofPackage={handleOpenProofPackage}
        onOpenReviewSeriesQueue={handleOpenReviewSeriesQueue}
        onOpenIncidentReviewPage={handleOpenIncidentReviewPage}
        incidentDrawerCtx={incidentDrawerCtx}
        incidentDialogs={incidentDialogs}
        suppressDialogOpen={suppressDialogOpen}
        onCloseSuppressDialog={() => setSuppressDialogOpen(false)}
        drawerBusy={drawerBusy}
        canManageIncidentSuppressions={canManageIncidentSuppressions}
        onSuppressSimilar={handleSuppressSimilarWith}
        drawerIncidentLoading={drawerIncidentLoading}
        severityIcon={_severityIcon}
        severityChipSx={_severityChipSx}
        opsStateLabel={_opsStateLabel}
      />
    );
  }

  return (
    <Box sx={{ pb: 3 }}>
      <IncidentHeader
        activeScope={activeScope}
        setScopeDialogOpen={setScopeDialogOpen}
        incidentsAdvancedEligible={incidentsAdvancedEligible}
        incidentsUiMode={incidentsUiMode}
        setIncidentsUiModePersist={setIncidentsUiModePersist}
        onRefresh={fetchData}
        loading={loading}
        problemContext={workflowProblemContext}
        nextStep={{
          title: _incidentsNextStep.title,
          detail: _incidentsNextStep.detail,
          label: _incidentsNextStep.label,
          onClick: _incidentsNextStep.action,
        }}
      />

      <IncidentToolbar
        canUseOpsAutomation={canUseOpsAutomation}
        opsRecoveryError={opsRecoveryError}
        opsRecoveryResult={opsRecoveryResult}
        clearOpsRecovery={clearOpsRecovery}
        scopeDialogOpen={scopeDialogOpen}
        closeScopeDialog={() => setScopeDialogOpen(false)}
        scopeOrgDraft={scopeOrgDraft}
        setScopeOrgDraft={setScopeOrgDraft}
        scopeSiteDraft={scopeSiteDraft}
        setScopeSiteDraft={setScopeSiteDraft}
        saveScope={handleSaveScope}
        error={error}
        clearError={() => setError(null)}
        opsStateResult={opsStateResult}
        diagnosticsHref={`/diagnostics${scopedSiteId ? `?site=${encodeURIComponent(scopedSiteId)}` : ''}`}
        copyContext={handleCopyOpsContext}
        operatorMode={incidentsUiMode !== 'advanced'}
      />

      <HeroMetricRow
        kpis={kpis}
        opsQueues={opsQueues}
        groups={groups}
        filteredGroups={tableMode === 'grouped' ? (displayedRows as IncidentGroup[]) : []}
        filteredIncidents={tableMode === 'raw' ? (visibleRows as Incident[]) : []}
        filteredIncidentTotal={
          tableMode === 'raw' && rawObservationTotalCompatible
            ? incidentsTotal
            : null
        }
        tableMode={tableMode}
        layout="strip"
        reviewOutcomeRollup={reviewOutcomeRollup}
        reviewPopulationCompatible={reviewPopulationCompatible}
        groupedReviewerHealth={groupedCaseQueueSummary ? {
          staleAwaitingReview: groupedCaseQueueSummary.staleAwaitingReview,
          awaitingReview: groupedCaseQueueSummary.awaitingReview,
        } : null}
        onOpenIncidents={handleOpenIncidentsMetric}
        onHighRiskUnresolved={handleHighRiskMetric}
        onRecurringHotspots={handleRecurringMetric}
        onReviewThroughput={handleReviewThroughputMetric}
        onOpenReviewerHealth={handleOpenReviewerHealthMetric}
      />

      <Box ref={queueBlockRef}>
        <IncidentQueueBlock
          eyebrow={queueWorkflowHandoff?.eyebrow}
          title={queueWorkflowHandoff?.title}
          description={queueWorkflowHandoff?.description}
          contextChip={queueWorkflowHandoff?.contextChip}
          accent={queueWorkflowHandoff ? 'workflow' : 'default'}
        >
        {filterHeatmapHour != null && (
          <Alert
            severity="info"
            onClose={handleClearHeatmapHourFilter}
            sx={{ mb: 1.5, py: 0.5 }}
          >
            <Typography variant="body2" component="span">
              Filtered to incidents with{' '}
              <strong>created time in UTC hour {String(filterHeatmapHour).padStart(2, '0')}:00</strong> (same bucketing as the
              hourly pattern chart). Clear to see all rows in the date range.
            </Typography>
          </Alert>
        )}
        {String(filterPolicyPack || '').trim() && (
          <Alert severity="info" onClose={handleClearPolicyPackFilter} sx={{ mb: 1.5, py: 0.5 }}>
            <Typography variant="body2" component="span">
              Showing queue rows for pack{' '}
              <strong>
                {filterPolicyPack === 'unknown'
                  ? 'Not linked to a pack'
                  : PACK_BY_KEY[filterPolicyPack as PackKey]?.label || filterPolicyPack}
              </strong>
              . Clear to return to the full queue for this date range.
            </Typography>
          </Alert>
        )}
        {tableMode === 'grouped' && showGroupedReviewDigest ? (
          <Alert
            severity="warning"
            variant="outlined"
            sx={{ mb: 1.5, py: 0.65, alignItems: 'flex-start', '& .MuiAlert-message': { width: '100%' } }}
            action={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => {
                    setGroupedCaseStateFilter('awaiting_review');
                    scrollToSection(queueBlockRef);
                  }}
                >
                  Focus patterns
                </Button>
                <IconButton
                  size="small"
                  aria-label="Dismiss grouped review digest"
                  onClick={() => {
                    setGroupedReviewDigestDismissedToken(groupedReviewDigestToken);
                    try {
                      window.localStorage.setItem(GROUPED_REVIEW_DIGEST_DISMISS_KEY, groupedReviewDigestToken);
                    } catch {
                      // ignore
                    }
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            }
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.2 }}>
              {groupedCaseQueueSummary?.awaitingReview === 1
                ? '1 pattern needs review — about 5 minutes each'
                : `${groupedCaseQueueSummary?.awaitingReview} patterns need review — about 5 minutes each`}
            </Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
              Review representative evidence, not every observation row. Until these patterns are reviewed, corrective action cannot be verified.
              {groupedCaseQueueSummary?.staleAwaitingReview
                ? ` ${groupedCaseQueueSummary.staleAwaitingReview} pattern${groupedCaseQueueSummary.staleAwaitingReview === 1 ? ' has' : 's have'} been waiting 7+ days.`
                : ''}
            </Typography>
          </Alert>
        ) : null}
        {tableMode === 'grouped' && groupedCaseQueueSummary ? (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, maxWidth: 780, lineHeight: 1.5 }}>
              Review recurring activity first. Open an item to inspect evidence and decide the next step.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={groupedCaseStateFilter}
                onChange={(_, next) => {
                  if (next === 'all' || next === 'awaiting_review' || next === 'monitoring') {
                    setGroupedCaseStateFilter(next);
                  }
                }}
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="awaiting_review">Needs review</ToggleButton>
                <ToggleButton value="monitoring">Monitoring</ToggleButton>
              </ToggleButtonGroup>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                <Chip
                  color={groupedCaseQueueSummary.awaitingReview > 0 ? 'warning' : 'default'}
                  label={`${groupedCaseQueueSummary.awaitingReview} awaiting review`}
                  sx={{ fontWeight: 800 }}
                />
                <Chip
                  color={groupedCaseQueueSummary.trackingFollowUp > 0 ? 'info' : 'default'}
                  label={`${groupedCaseQueueSummary.trackingFollowUp} reviewer engaged`}
                  sx={{ fontWeight: 800 }}
                />
                <Chip
                  variant="outlined"
                  label={`${groupedCaseQueueSummary.preparingEvidence} preparing evidence`}
                  sx={{ fontWeight: 800 }}
                />
                <Chip
                  color={groupedCaseQueueSummary.closed > 0 ? 'success' : 'default'}
                  label={`${groupedCaseQueueSummary.closed} no action needed`}
                  sx={{ fontWeight: 800 }}
                />
                {groupedCaseQueueSummary.staleAwaitingReview > 0 ? (
                  <Chip
                    color="warning"
                    variant="outlined"
                    label={`${groupedCaseQueueSummary.staleAwaitingReview} stale for 7+ days`}
                    sx={{ fontWeight: 800 }}
                  />
                ) : null}
              </Box>
            </Box>
          </Box>
        ) : null}
        <IncidentFiltersBar
          ctx={{
            tabValue, setTabValue,
            incidentsUiMode,
            tableMode, setTableMode,
            sortBySeverity, setSortBySeverity,
            sortModel, setSortModel,
            _defaultSortModel,
            operatorPreset, setOperatorPreset, _applyPreset,
            timePreset, setTimePreset,
            customFrom, setCustomFrom,
            customTo, setCustomTo,
            filterMinAgeMinutes, setFilterMinAgeMinutes,
            timeRange,
            canOpenArtifactEntry, _openReportDialog: handleOpenProofPackage,
            searchIncidentId, setSearchIncidentId,
            tableFiltersOpen, setTableFiltersOpen,
            advancedTableFilterCount,
            filterIncidentType, setFilterIncidentType,
            filterZoneId, setFilterZoneId,
            filterCameraId, setFilterCameraId,
            filterDeviceId, setFilterDeviceId,
            filterSeverity, setFilterSeverity,
            filterIncidentStatus, setFilterIncidentStatus,
            filterWorkflowStatus, setFilterWorkflowStatus,
            filterOpsAssignee, setFilterOpsAssignee,
            filterOpsQueue, setFilterOpsQueue,
            incidentTypeOptions, zoneOptions, cameraOptions, deviceOptions,
            zoneNameById, cameraNameById, deviceNameById,
            _renderAllLabel, _incidentTypeLabel: incidentTypeLabel, _titleCase, _warehouseUnitLabel, _shortId,
            searchParams, setSearchParams, fetchData,
          }}
        />

        <IncidentTable
          rows={displayedRows as any[]}
          columns={columns}
          groupColumns={groupColumns}
          tableMode={tableMode}
          loading={loading}
          sortModel={sortModel}
          setSortModel={setSortModel}
          onRowClick={(params: any) => openIncidentReviewSurface(params?.row)}
          selectedRowId={
            viewDialogOpen
              ? String(
                  (tableMode === 'grouped' && selectedGroupContext && (selectedGroupContext as any).group_key) ||
                    (tableMode !== 'grouped' && (searchParams.get('episode_id') || (selectedIncident as any)?.observation_episode_id)) ||
                    selectedIncident?.incident_id ||
                    '',
                ) || null
              : null
          }
          selectionActive={viewDialogOpen}
        />
        </IncidentQueueBlock>
      </Box>

      {tableMode === 'grouped' && displayedRows.length > 0 && selectedGroupContext ? (
        <Box ref={insightBandRef} sx={{ mt: 2.5 }}>
          <InsightBand
            tableMode="grouped"
            presentation="groupedDrilldown"
            selectedGroup={selectedGroupContext}
            hasRows={displayedRows.length > 0}
            groups={groups}
            zoneNameById={zoneNameById}
            cameraNameById={cameraNameById}
            onSelectHotspot={handleHotspotSelect}
            hotspotRef={hotspotsCardRef}
          />
        </Box>
      ) : null}

      <IncidentEvidencePopover
        evidencePopover={evidencePopover}
        closeEvidencePopover={closeEvidencePopover}
        evidencePreviewUrlByIncident={evidencePreviewUrlByIncident}
        evidencePreviewLoadingByIncident={evidencePreviewLoadingByIncident}
        evidencePreviewErrorByIncident={evidencePreviewErrorByIncident}
        zoneNameById={zoneNameById}
        fetchLatestSnapshotBlob={fetchLatestSnapshotBlob}
        fetchLatestVideoBlob={fetchLatestVideoBlob}
        downloadBlob={downloadBlob}
        onOpenMetadata={openIncidentJson}
      />
      <IncidentCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        newIncident={newIncident}
        setNewIncident={setNewIncident}
        onCreate={handleCreateIncident}
      />
      {/* Incident review surface */}
      <IncidentDrawer ctx={incidentDrawerCtx} />

      {/* Suppression dialog (extracted) */}
      <IncidentSuppressDialog
        open={suppressDialogOpen}
        onClose={() => setSuppressDialogOpen(false)}
        selectedIncident={selectedIncident}
        drawerBusy={drawerBusy}
        canManage={canManageIncidentSuppressions}
        incidentTypeLabel={incidentTypeLabel}
        onSuppress={async ({ durationSec, reason }) => {
          await handleSuppressSimilarWith(durationSec, reason);
        }}
      />

      {/* Threshold dialog (extracted) */}
      {incidentDialogs}

      <Snackbar
        open={patternActionSnack.open}
        autoHideDuration={patternActionSnack.severity === 'error' ? 15000 : 8000}
        onClose={() => setPatternActionSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setPatternActionSnack((s) => ({ ...s, open: false }))}
          severity={patternActionSnack.severity}
          variant="filled"
          sx={{ width: '100%', maxWidth: 560 }}
        >
          {patternActionSnack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Incidents;
